/**
 * 🏘️ Inmuebles — UI super visual con 3 pestañas:
 *   1) Mis Inmuebles  — los activos del usuario
 *   2) Oportunidades  — chollos detectados por los Scouts (idealista, fotocasa, etc.)
 *   3) Predicciones   — proyecciones de mercado 2/4/6/12 meses
 *
 * Modal nativo eliminado: todas las interacciones usan un modal HTML propio.
 */
import { h, apiClient, formatEur, formatDate, pill, emptyState, btn, toast, openQuickTaskModal, openModal} from './_common.js';
import { renderTasksForSection } from './tareas.js';
import { renderGastosUI } from './gastos.js';

let _activeTab = 'mine';
let _oppFilters = { source: '', status: '', minScore: 0 };

export async function renderInmuebles(root) {
  // Limpiar el contenedor antes de pintar (evita duplicación al re-renderizar)
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' }, [
    h('div', { class: 'flex items-center justify-between mb-6 flex-wrap gap-3' }, [
      h('div', {}, [
        h('h2', { class: 'font-display text-3xl font-bold' }, '🏘️ Inmuebles'),
        h('p', { class: 'text-ink-300' }, 'Activos · Oportunidades del mercado · Predicciones'),
      ]),
      h('div', { class: 'flex gap-2 flex-wrap' }, [
        btn('✅ + Tarea', () => openQuickTaskModal('inmuebles'), { variant: 'secondary' }),
        btn('💰 Importar Airbnb', () => openAirbnbImportModal(root), { variant: 'secondary' }),
        btn('🤖 Lanzar Scouts', () => runScouts(root)),
        btn('+ Añadir inmueble', () => openPropertyModal(root, () => renderInmuebles(root))),
      ]),
    ]),

    // Tabs
    h('div', { class: 'flex gap-1 border-b border-base-700 mb-5 overflow-x-auto' }, [
      tabBtn('mine', '🏠 Mis Inmuebles', root),
      tabBtn('calendars', '📅 Calendarios Airbnb', root),
      tabBtn('earnings', '💰 Ingresos', root),
      tabBtn('calc', '🧮 Calculadora rentabilidad', root),
      tabBtn('opportunities', '💎 Oportunidades', root),
    ]),

    h('div', { id: 'tab-content' }),
  ]);
  root.appendChild(wrap);
  await renderActiveTab(root);
}

function tabBtn(key, label, root) {
  const active = _activeTab === key;
  return h('button', {
    class: `px-4 py-2 text-sm font-medium transition-colors border-b-2 ${active ? 'border-gold-400 text-gold-300' : 'border-transparent text-ink-300 hover:text-ink-100'}`,
    onclick: async () => { _activeTab = key; await renderActiveTab(root); },
  }, label);
}

async function renderActiveTab(root) {
  // Re-render tab buttons
  const headerBar = root.querySelector('.border-b.border-base-700');
  if (headerBar) {
    headerBar.innerHTML = '';
    ['mine', 'calendars', 'earnings', 'calc', 'opportunities', 'contactos', 'inventario', 'gastos', 'tasks'].forEach((k) => {
      const labels = { mine: '🏠 Mis Inmuebles', calendars: '📅 Calendarios Airbnb', earnings: '💰 Ingresos', calc: '🧮 Calculadora rentabilidad', opportunities: '💎 Oportunidades', contactos: '📇 Contactos inmobiliarias', inventario: '📦 Inventario', gastos: '💸 Gastos', tasks: '✅ Tareas' };
      headerBar.appendChild(tabBtn(k, labels[k], root));
    });
  }
  const c = root.querySelector('#tab-content');
  if (!c) return;
  c.innerHTML = '';
  c.appendChild(h('div', { class: 'text-ink-300 text-sm py-6 text-center' }, 'Cargando...'));
  try {
    if (_activeTab === 'mine') {
      await renderMyProperties(c);
    } else if (_activeTab === 'calendars') {
      await renderCalendars(c);
    } else if (_activeTab === 'earnings') {
      await renderEarnings(c, root);
    } else if (_activeTab === 'calc') {
      renderRentabilidadCalc(c);
    } else if (_activeTab === 'opportunities') {
      await renderOpportunities(c);
    } else if (_activeTab === 'contactos') {
      await renderContactosInmobiliarias(c);
    } else if (_activeTab === 'inventario') {
      await renderInventario(c);
    } else if (_activeTab === 'gastos') {
      await renderGastosUI(c, { scope: 'inmueble' });
    } else if (_activeTab === 'tasks') {
      await renderTasksForSection(c, 'inmuebles');
    } else if (_activeTab === 'predictions') {
      // Pestaña eliminada — fallback a opportunities
      _activeTab = 'opportunities';
      await renderOpportunities(c);
    } else {
      await renderOpportunities(c);
    }
  } catch (err) {
    c.innerHTML = '';
    c.appendChild(emptyState('⚠️', 'Error', err?.message || 'No se pudo cargar.'));
  }
}

// ============================================================================
// 📇 CONTACTOS INMOBILIARIAS · búsqueda activa off-market
// ============================================================================
async function renderContactosInmobiliarias(container) {
  container.innerHTML = '';
  // Cargar lista + plantilla en paralelo
  const [listResp, tplResp, statsResp] = await Promise.all([
    apiClient.get('/api/v1/inmuebles/contactos').catch(() => ({ contactos: [] })),
    apiClient.get('/api/v1/inmuebles/contactos/whatsapp-template').catch(() => ({ template: '', reminder_days: 7 })),
    apiClient.get('/api/v1/inmuebles/contactos/stats').catch(() => ({})),
  ]);
  const contactos = listResp.contactos || [];
  const stats = statsResp || {};
  const tplData = tplResp || { template: '' };

  // HEADER + STATS
  container.appendChild(h('div', { class: 'mb-5' }, [
    h('div', { class: 'flex items-start justify-between flex-wrap gap-3 mb-3' }, [
      h('div', {}, [
        h('h3', { class: 'font-display font-bold text-2xl mb-1' }, '📇 Contactos de inmobiliarias'),
        h('p', { class: 'text-sm text-ink-300' }, 'Registro de agentes para búsqueda activa off-market. Pásales por WhatsApp lo que buscas semanalmente.'),
      ]),
      h('div', { class: 'flex gap-2 flex-wrap' }, [
        h('button', { class: 'px-4 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400 text-sm', onclick: () => openContactoModal(container) }, '+ Añadir contacto'),
        h('button', { class: 'px-4 py-2 bg-base-700 text-ink-100 rounded-lg hover:bg-base-600 text-sm', onclick: () => openTemplateModal(container, tplData) }, '✏️ Plantilla WhatsApp'),
        h('button', { class: 'px-4 py-2 bg-rose-500/20 text-rose-200 border border-rose-400/40 rounded-lg hover:bg-rose-500/30 text-sm font-semibold', onclick: () => openEmailMasivoModal(container, contactos, tplData) }, '📧 Email masivo'),
      ]),
    ]),
    // Stats banner
    h('div', { class: 'grid grid-cols-3 gap-3' }, [
      h('div', { class: 'bg-base-800/60 border border-base-700 rounded-lg p-3 text-center' }, [
        h('div', { class: 'text-lg font-bold text-ink-100' }, String(stats.total ?? 0)),
        h('div', { class: 'text-xs text-ink-400 uppercase tracking-wide' }, 'Contactos totales'),
      ]),
      h('div', { class: 'bg-yellow-500/10 border border-yellow-400/30 rounded-lg p-3 text-center' }, [
        h('div', { class: 'text-lg font-bold text-yellow-300' }, String(stats.pendientes_followup ?? 0)),
        h('div', { class: 'text-xs text-yellow-200 uppercase tracking-wide' }, 'A contactar (>7d)'),
      ]),
      h('div', { class: 'bg-green-500/10 border border-green-400/30 rounded-lg p-3 text-center' }, [
        h('div', { class: 'text-lg font-bold text-green-300' }, String(stats.contactos_enviados_7d ?? 0)),
        h('div', { class: 'text-xs text-green-200 uppercase tracking-wide' }, 'Enviados 7 días'),
      ]),
    ]),
  ]));

  if (contactos.length === 0) {
    container.appendChild(emptyState('📇', 'Sin contactos aún',
      'Añade los datos de los agentes inmobiliarios con los que hablas. Te avisaremos cuando lleve +7 días sin contacto.'));
    return;
  }

  // LISTA
  const list = h('div', { class: 'space-y-2' });
  contactos.forEach(c => {
    const needsFollowup = c.needs_followup;
    const days = c.days_since_contact;
    const phoneNorm = (c.telefono || '').replace(/[^\d+]/g, '').replace(/^\+/, '');
    const tplFilled = (tplData.template || '').replace(/\{nombre\}/g, c.nombre.split(/\s+/)[0]);
    const waUrl = `https://wa.me/${phoneNorm}?text=${encodeURIComponent(tplFilled)}`;

    list.appendChild(h('div', {
      class: `bg-base-800/60 border rounded-xl p-4 ${needsFollowup ? 'border-yellow-400/40' : 'border-base-700'} hover:border-gold-400/50 transition-colors`,
    }, [
      h('div', { class: 'flex items-start justify-between flex-wrap gap-3' }, [
        h('div', { class: 'flex-1 min-w-0' }, [
          h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
            h('div', { class: 'font-semibold text-ink-100 text-base' }, c.nombre),
            c.agencia ? h('span', { class: 'text-xs bg-base-700 text-ink-300 px-2 py-0.5 rounded' }, c.agencia) : null,
            c.ciudad ? h('span', { class: 'text-xs text-ink-400' }, '· ' + c.ciudad) : null,
          ].filter(Boolean)),
          h('div', { class: 'text-sm text-ink-300 mt-1' }, [
            h('a', { href: 'tel:' + c.telefono, class: 'hover:text-gold-300' }, '📞 ' + c.telefono),
            c.email ? h('span', { class: 'ml-3 text-ink-400' }, '✉️ ' + c.email) : null,
          ].filter(Boolean)),
          c.notas ? h('div', { class: 'text-xs text-ink-400 mt-1 italic' }, c.notas) : null,
          h('div', { class: `text-xs mt-2 ${needsFollowup ? 'text-yellow-300' : 'text-ink-400'}` },
            days === null ? '⚠️ Nunca contactado'
            : days === 0 ? '✅ Contactado hoy'
            : `Último contacto: hace ${days} día${days === 1 ? '' : 's'}${needsFollowup ? ' · ⚠️ tocar' : ''}`),
        ].filter(Boolean)),
        h('div', { class: 'flex gap-2 flex-wrap' }, [
          h('a', {
            href: waUrl, target: '_blank',
            class: 'px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded text-sm font-semibold inline-flex items-center gap-1',
            onclick: async (e) => {
              // Marca como contactado al click
              try { await apiClient.post(`/api/v1/inmuebles/contactos/${c.id}/contacted`, { channel: 'whatsapp', message_preview: tplFilled.slice(0, 200) }); } catch {}
              setTimeout(() => renderContactosInmobiliarias(container), 800);
            },
          }, '💬 WhatsApp'),
          h('button', {
            class: 'px-3 py-1.5 bg-base-700 hover:bg-base-600 text-ink-100 rounded text-sm',
            onclick: () => openContactoModal(container, c),
          }, '✏️'),
          h('button', {
            class: 'px-3 py-1.5 bg-red-900/40 hover:bg-red-900/60 text-red-300 rounded text-sm',
            onclick: async () => {
              if (!confirm(`¿Eliminar contacto "${c.nombre}"?`)) return;
              await apiClient.delete(`/api/v1/inmuebles/contactos/${c.id}`);
              renderContactosInmobiliarias(container);
            },
          }, '🗑️'),
        ]),
      ]),
    ]));
  });
  container.appendChild(list);
}

function openContactoModal(container, existing = null) {
  const overlay = h('div', { class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4', onclick: (e) => { if (e.target === overlay) overlay.remove(); } });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl p-6 max-w-md w-full' });
  modal.appendChild(h('h3', { class: 'font-display font-bold text-xl mb-4' }, existing ? 'Editar contacto' : 'Añadir contacto'));
  const fields = ['nombre', 'telefono', 'agencia', 'ciudad', 'email', 'notas'];
  const labels = { nombre: 'Nombre*', telefono: 'Teléfono*', agencia: 'Agencia', ciudad: 'Ciudad', email: 'Email', notas: 'Notas' };
  const inputs = {};
  fields.forEach(f => {
    const isNotes = f === 'notas';
    const inp = h(isNotes ? 'textarea' : 'input', {
      class: 'w-full px-3 py-2 bg-base-800 border border-base-700 rounded-lg text-ink-100 focus:border-gold-400 outline-none text-sm',
      placeholder: labels[f],
      value: existing?.[f] || '',
      rows: isNotes ? 3 : null,
    });
    inputs[f] = inp;
    modal.appendChild(h('div', { class: 'mb-3' }, [
      h('label', { class: 'block text-xs text-ink-300 mb-1' }, labels[f]),
      inp,
    ]));
  });
  modal.appendChild(h('div', { class: 'flex gap-2 mt-4' }, [
    h('button', { class: 'flex-1 px-4 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400', onclick: async () => {
      const body = {};
      fields.forEach(f => { body[f] = inputs[f].value.trim(); });
      if (!body.nombre || !body.telefono) { alert('Nombre y teléfono son obligatorios'); return; }
      try {
        if (existing) await apiClient.patch(`/api/v1/inmuebles/contactos/${existing.id}`, body);
        else await apiClient.post('/api/v1/inmuebles/contactos', body);
        overlay.remove();
        renderContactosInmobiliarias(container);
      } catch (e) { alert('Error: ' + (e?.message || 'no se pudo guardar')); }
    } }, existing ? 'Guardar cambios' : 'Crear contacto'),
    h('button', { class: 'px-4 py-2 bg-base-700 text-ink-100 rounded-lg hover:bg-base-600', onclick: () => overlay.remove() }, 'Cancelar'),
  ]));
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  inputs.nombre.focus();
}

function openTemplateModal(container, tplData) {
  const overlay = h('div', { class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4', onclick: (e) => { if (e.target === overlay) overlay.remove(); } });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl p-6 max-w-lg w-full' });
  modal.appendChild(h('h3', { class: 'font-display font-bold text-xl mb-2' }, '✏️ Plantilla WhatsApp'));
  modal.appendChild(h('p', { class: 'text-xs text-ink-400 mb-4' }, 'Mensaje que se envía al pulsar 💬 WhatsApp. Puedes usar {nombre} y se reemplaza por el primer nombre del contacto.'));
  const textarea = h('textarea', {
    class: 'w-full px-3 py-2 bg-base-800 border border-base-700 rounded-lg text-ink-100 focus:border-gold-400 outline-none text-sm font-mono',
    rows: 8,
    value: tplData.template || '',
  });
  modal.appendChild(textarea);
  modal.appendChild(h('div', { class: 'flex items-center gap-2 mt-3 text-xs text-ink-300' }, [
    h('span', {}, 'Recordatorio cada'),
    (function () {
      const inp = h('input', { type: 'number', min: 1, max: 90, class: 'w-16 px-2 py-1 bg-base-800 border border-base-700 rounded text-ink-100', value: tplData.reminder_days || 7 });
      modal._reminderInput = inp;
      return inp;
    })(),
    h('span', {}, 'días sin contacto'),
  ]));
  modal.appendChild(h('div', { class: 'flex gap-2 mt-5' }, [
    h('button', { class: 'flex-1 px-4 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400', onclick: async () => {
      try {
        await apiClient.put('/api/v1/inmuebles/contactos/whatsapp-template', { template: textarea.value, reminder_days: Number(modal._reminderInput.value) || 7 });
        overlay.remove();
        renderContactosInmobiliarias(container);
      } catch (e) { alert('Error: ' + e?.message); }
    } }, 'Guardar plantilla'),
    h('button', { class: 'px-4 py-2 bg-base-700 text-ink-100 rounded-lg hover:bg-base-600', onclick: () => { textarea.value = tplData.default_template || ''; } }, 'Restaurar default'),
    h('button', { class: 'px-4 py-2 bg-base-700 text-ink-100 rounded-lg hover:bg-base-600', onclick: () => overlay.remove() }, 'Cerrar'),
  ]));
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// ============================================================================
// 🧮 CALCULADORA RENTABILIDAD INMOBILIARIA PRO
// Análisis profesional de inmuebles ANTES de comprar:
//   • Yields (bruto, neto, cap rate)
//   • Cash-on-cash (antes y después de impuestos)
//   • IRR, NPV, payback period
//   • Cash flow proyectado a 10 años
//   • DSCR, LTV, breakeven occupancy
//   • Reglas rápidas (1%, 2%, 50%)
//   • Stress tests (caída renta/ocupación, subida tipos)
//   • Comparativa Long-term vs Short-term
//   • Veredicto inteligente con score 0-100
// ============================================================================

// ESTADO compartido entre re-renders
const _calcState = {
  rentalMode: 'short_term',
};

function renderRentabilidadCalc(container) {
  container.innerHTML = '';

  // HEADER
  container.appendChild(h('div', { class: 'mb-5' }, [
    h('h3', { class: 'font-display font-bold text-2xl mb-2' }, '🧮 ¿Me compro este piso?'),
    h('p', { class: 'text-sm text-ink-300 leading-relaxed' }, 'Calculadora completa para decidir si un piso que te interesa es buena inversión. Rellena los datos del piso y CARLOS OS te dice claramente: comprar / negociar / pasar.'),
  ]));

  // === PANEL EDUCATIVO: "Cómo funciona esto" ===
  container.appendChild(buildGlossaryPanel());

  // === GUÍA RÁPIDA ===
  container.appendChild(h('div', { class: 'bg-gold-500/5 border border-gold-400/20 rounded-xl p-4 mb-5' }, [
    h('div', { class: 'flex items-start gap-3' }, [
      h('span', { class: 'text-2xl' }, '💡'),
      h('div', { class: 'flex-1' }, [
        h('p', { class: 'text-sm font-semibold text-gold-200 mb-1' }, 'Cómo usar la calculadora — en 3 pasos:'),
        h('ol', { class: 'text-xs text-ink-300 space-y-1 list-decimal list-inside' }, [
          h('li', {}, [h('strong', {}, '1.'), ' Pulsa "Cargar ejemplo Alicante" abajo para ver datos realistas precargados (o mete los tuyos).']),
          h('li', {}, [h('strong', {}, '2.'), ' Cambia los campos que conozcas (precio real del piso, tu hipoteca, etc.). Los demás déjalos como están — son medias del mercado español.']),
          h('li', {}, [h('strong', {}, '3.'), ' Pulsa "CALCULAR" y verás un VEREDICTO grande (verde/amarillo/rojo) con la decisión. Cada métrica del resultado tiene un (?) que te explica qué significa.']),
        ]),
      ]),
    ]),
  ]));

  // FORM con secciones colapsables
  const form = h('div', { class: 'space-y-3' });

  // === Sección 1: BÁSICOS ===
  form.appendChild(calcSection('📍 Datos básicos del inmueble', true, [
    inp2('Precio de compra (€)', 'purchasePrice', '180000', 'required'),
    inp2('Valor de mercado (€)', 'marketValue', '180000', 'Para calcular cap rate. Si compras a precio mercado, igual al precio compra'),
    inp2('Metros cuadrados', 'squareMeters', '70'),
    inp2('Habitaciones', 'bedrooms', '2'),
    inp2('Ciudad', 'city', 'Alicante', '', 'text'),
    selectField('Tipo', 'propertyType', 'piso', [
      { value: 'piso', label: 'Piso' },
      { value: 'estudio', label: 'Estudio' },
      { value: 'casa', label: 'Casa' },
      { value: 'local', label: 'Local' },
    ]),
  ]));

  // === Sección 2: COSTES ADQUISICIÓN ===
  form.appendChild(calcSection('💸 Costes de adquisición (España)', true, [
    inp2('ITP / IVA (%)', 'transferTaxPct', '10', '10% en CV usado · 10% obra nueva (IVA)'),
    inp2('Notaría (%)', 'notaryPct', '0.5', '0.4-0.8% típico'),
    inp2('Registro (%)', 'registryPct', '0.3', '0.2-0.4%'),
    inp2('Gestoría (€)', 'legalFees', '400'),
    inp2('Tasación (€)', 'appraisalCost', '350'),
    inp2('Comisión inmobiliaria (%)', 'brokerCommissionPct', '0', 'Si compras a través de agencia, suele ser 3% el comprador'),
  ]));

  // === Sección 3: REFORMA Y MUEBLES ===
  form.appendChild(calcSection('🔨 Reforma y equipamiento', true, [
    inp2('Presupuesto reforma (€)', 'reformBudget', '0'),
    inp2('Imprevistos sobre reforma (%)', 'contingencyPct', '10', 'Colchón típico 10-15% del presupuesto'),
    inp2('Muebles (€)', 'furnitureBudget', '0'),
    inp2('Electrodomésticos (€)', 'appliancesBudget', '0'),
    inp2('Diseño/decoración (€)', 'designBudget', '0'),
  ]));

  // === Sección 4: FINANCIACIÓN ===
  form.appendChild(calcSection('🏦 Financiación (hipoteca)', true, [
    inp2('Entrada (%)', 'downPaymentPct', '30', 'Típico 20-30%. Banco te da hasta 80% LTV en 2ª vivienda'),
    inp2('Tipo hipoteca anual (%)', 'mortgageRateAnnualPct', '3.5', 'TAE mercado actual ~3-4% fijo'),
    inp2('Años hipoteca', 'mortgageYears', '30'),
    inp2('Comisión apertura (%)', 'mortgageOpeningFeePct', '0.5', '0-1% típico'),
    inp2('Otros costes banco (€)', 'bankFees', '500', 'Vinculaciones, seguros, etc.'),
  ]));

  // === Sección 5: MODELO DE ALQUILER ===
  form.appendChild(rentalModeSection());

  // === Sección 6: GASTOS OPERATIVOS ===
  form.appendChild(calcSection('🧾 Gastos operativos anuales', true, [
    inp2('Comunidad (€/mes)', 'hoaFeesMonthly', '60'),
    inp2('IBI anual (€)', 'propertyTaxAnnual', '300'),
    inp2('Basuras anual (€)', 'garbageTaxAnnual', '60'),
    inp2('Seguro hogar anual (€)', 'homeInsuranceAnnual', '250'),
    inp2('Seguro impago (LT) anual (€)', 'rentDefaultInsuranceAnnual', '0', 'Solo si alquilas LT: 3-5% del alquiler anual'),
    inp2('Mantenimiento (% de la renta)', 'maintenancePctOfRent', '5', 'Típico 5-10% para colchón averías'),
  ]));

  // === Sección 7: STR específicos (solo si short_term) ===
  form.appendChild(strExtrasSection());

  // === Sección 8: IMPUESTOS ===
  form.appendChild(calcSection('🏛 Impuestos (IRPF)', true, [
    inp2('Tipo marginal IRPF (%)', 'irpfMarginalRatePct', '30', 'Tu tramo marginal: 19%, 24%, 30%, 37%, 45%, 47%'),
    h('div', { class: 'col-span-full text-xs text-ink-400 mt-1' }, [
      h('label', { class: 'flex items-center gap-2 cursor-pointer' }, [
        h('input', { type: 'checkbox', id: 'r-applyLtRentDeduction', class: 'rounded' }),
        h('span', {}, 'Aplicar deducción -60% (solo Long-term en vivienda habitual del inquilino)'),
      ]),
    ]),
  ]));

  // === Sección 9: PROYECCIÓN A LARGO PLAZO ===
  form.appendChild(calcSection('📈 Proyección a largo plazo', false, [
    inp2('Inflación rentas anual (%)', 'rentInflationPct', '2.5'),
    inp2('Inflación gastos anual (%)', 'expenseInflationPct', '2.5'),
    inp2('Revalorización inmueble (%/año)', 'appreciationPct', '3', 'Media histórica España 2-4%'),
    inp2('Tasa de descuento (%)', 'discountRatePct', '5', 'Para calcular NPV — coste de oportunidad'),
    inp2('Horizonte análisis (años)', 'holdYears', '10', '5-20 años típico'),
  ]));

  // === Sección 10: COSTES DE SALIDA ===
  form.appendChild(calcSection('🚪 Si vendes al final (opcional)', false, [
    inp2('Comisión venta (%)', 'saleCommissionPct', '4', 'Inmobiliaria suele cobrar 3-5%'),
    inp2('IRPF plusvalía (%)', 'capitalGainsTaxPct', '21', 'España: 19% hasta 6k, 21% 6k-50k, 23% 50k-200k, 26% >200k'),
  ]));

  container.appendChild(form);

  // === BOTÓN CALCULAR ===
  const result = h('div', { id: 'r-result', class: 'mt-6' });

  container.appendChild(h('div', { class: 'flex items-center gap-3 mt-5 mb-4' }, [
    btn('🧮 CALCULAR RENTABILIDAD PROFESIONAL', async () => {
      await runProCalc(result);
    }),
    btn('💾 Cargar ejemplo Alicante', () => fillExampleAlicante(), { variant: 'secondary' }),
    btn('🗑 Limpiar', () => location.reload(), { variant: 'secondary' }),
  ]));
  container.appendChild(result);
}

// === HELPERS UI ===
function inp2(label, key, def = '', help = '', type = 'number') {
  return h('div', { class: 'flex flex-col' }, [
    h('label', { class: 'block text-xs text-ink-300 mb-1 font-medium' }, label),
    h('input', {
      type, step: 'any',
      id: 'r-' + key,
      value: def,
      placeholder: def,
      class: 'w-full bg-base-900 border border-base-600 hover:border-gold-400/40 focus:border-gold-400 rounded-lg px-3 py-2 text-sm outline-none transition',
    }),
    help ? h('div', { class: 'text-[10px] text-ink-500 mt-1' }, help) : null,
  ]);
}

function selectField(label, key, def, options) {
  return h('div', { class: 'flex flex-col' }, [
    h('label', { class: 'block text-xs text-ink-300 mb-1 font-medium' }, label),
    h('select', {
      id: 'r-' + key,
      class: 'w-full bg-base-900 border border-base-600 hover:border-gold-400/40 focus:border-gold-400 rounded-lg px-3 py-2 text-sm outline-none transition',
    }, options.map((o) => {
      const opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.label;
      if (o.value === def) opt.selected = true;
      return opt;
    })),
  ]);
}

function calcSection(title, open, fields) {
  const wrap = h('details', {
    class: 'bg-base-800/40 border border-base-700 rounded-xl overflow-hidden',
    open: open ? '' : undefined,
  });
  const summary = h('summary', {
    class: 'px-4 py-3 cursor-pointer font-display font-semibold text-base hover:bg-base-800/60 select-none',
  }, title);
  wrap.appendChild(summary);
  wrap.appendChild(h('div', { class: 'p-4 grid grid-cols-2 md:grid-cols-3 gap-3 border-t border-base-700' }, fields));
  return wrap;
}

function rentalModeSection() {
  const wrap = h('details', { class: 'bg-base-800/40 border border-base-700 rounded-xl overflow-hidden', open: '' });
  wrap.appendChild(h('summary', { class: 'px-4 py-3 cursor-pointer font-display font-semibold text-base hover:bg-base-800/60 select-none' }, '🏘 Modelo de alquiler e ingresos'));
  const body = h('div', { class: 'p-4 border-t border-base-700 space-y-3' });

  // Toggle modo alquiler
  body.appendChild(h('div', { class: 'flex gap-2 flex-wrap mb-2' }, [
    rentalModeBtn('long_term', '📅 Largo plazo (>1 año)'),
    rentalModeBtn('mid_term', '🗓 Medio plazo (1-12m)'),
    rentalModeBtn('short_term', '✈️ Alquiler turístico (Airbnb)'),
  ]));

  // Inputs según modo
  body.appendChild(h('div', { id: 'rental-inputs', class: 'grid grid-cols-2 md:grid-cols-3 gap-3' }));
  setTimeout(() => updateRentalInputs(), 50);

  wrap.appendChild(body);
  return wrap;
}

function rentalModeBtn(mode, label) {
  const active = _calcState.rentalMode === mode;
  return h('button', {
    class: `px-3 py-2 rounded-lg text-sm font-medium transition ${active ? 'bg-gold-500/20 border border-gold-400 text-gold-200' : 'bg-base-900 border border-base-600 text-ink-300 hover:border-gold-400/40'}`,
    onclick: () => {
      _calcState.rentalMode = mode;
      // Re-render toggles
      const wrap = document.querySelector('#rental-inputs')?.parentElement?.querySelector('.flex.gap-2');
      if (wrap) {
        wrap.innerHTML = '';
        ['long_term', 'mid_term', 'short_term'].forEach((m) => wrap.appendChild(rentalModeBtn(m, m === 'long_term' ? '📅 Largo plazo (>1 año)' : m === 'mid_term' ? '🗓 Medio plazo (1-12m)' : '✈️ Alquiler turístico (Airbnb)')));
      }
      updateRentalInputs();
    },
  }, label);
}

function updateRentalInputs() {
  const c = document.querySelector('#rental-inputs');
  if (!c) return;
  c.innerHTML = '';
  if (_calcState.rentalMode === 'long_term') {
    c.appendChild(inp2('Renta mensual (€)', 'monthlyRent', '900', 'Lo que te paga el inquilino al mes'));
    c.appendChild(inp2('Vacancy extra (%)', 'vacancyPctExtra', '2', 'Colchón meses vacíos al año'));
    c.appendChild(inp2('Otros ingresos (€/mes)', 'otherIncomeMonthly', '0', 'Parking, trastero…'));
  } else {
    c.appendChild(inp2('Precio noche (€)', 'avgNightlyRate', '100'));
    c.appendChild(inp2('Ocupación (%)', 'occupancyPct', '70', 'Realista Alicante: 60-80%'));
    c.appendChild(inp2('Noches medias por estancia', 'avgNightsPerStay', '3'));
    c.appendChild(inp2('Huéspedes medios por estancia', 'avgGuestsPerStay', '2'));
    c.appendChild(inp2('Otros ingresos (€/mes)', 'otherIncomeMonthly', '0'));
  }
}

function strExtrasSection() {
  return calcSection('✈️ Costes específicos alquiler turístico (STR)', false, [
    inp2('Limpieza por estancia (€)', 'cleaningPerStay', '40'),
    inp2('Estancias por mes (#)', 'cleaningStaysPerMonth', '0', '0 = se calcula auto desde ocupación'),
    inp2('Comisión plataforma (%)', 'platformFeePct', '3', 'Airbnb 3% host · Booking 15%'),
    inp2('Gestión profesional (%)', 'managementFeePct', '0', '15-25% si pagas a alguien que lo gestione'),
    inp2('Software pricing/mes (€)', 'pricingSoftwareMonthly', '0', 'PriceLabs ~20€/mes'),
    inp2('Tasa turística €/noche/persona', 'touristTaxPerNightPerGuest', '0', 'Baleares 1-2€, CV próximamente'),
    inp2('Suministros/mes (€)', 'utilitiesMonthly', '120', 'Luz/agua/internet (en STR los pagas tú)'),
  ]);
}

function fillExampleAlicante() {
  // Carga ejemplo realista de un piso 80m² Alicante centro
  const setVal = (k, v) => { const el = document.getElementById('r-' + k); if (el) el.value = v; };
  setVal('purchasePrice', 180000); setVal('marketValue', 180000); setVal('squareMeters', 75); setVal('bedrooms', 2);
  setVal('transferTaxPct', 10); setVal('notaryPct', 0.5); setVal('registryPct', 0.3);
  setVal('legalFees', 400); setVal('appraisalCost', 350); setVal('brokerCommissionPct', 0);
  setVal('reformBudget', 15000); setVal('contingencyPct', 10); setVal('furnitureBudget', 5000); setVal('appliancesBudget', 3000);
  setVal('downPaymentPct', 30); setVal('mortgageRateAnnualPct', 3.5); setVal('mortgageYears', 30);
  setVal('mortgageOpeningFeePct', 0.5); setVal('bankFees', 500);
  setVal('avgNightlyRate', 90); setVal('occupancyPct', 70); setVal('avgNightsPerStay', 3); setVal('avgGuestsPerStay', 2);
  setVal('hoaFeesMonthly', 60); setVal('propertyTaxAnnual', 300); setVal('garbageTaxAnnual', 60);
  setVal('homeInsuranceAnnual', 250); setVal('maintenancePctOfRent', 5);
  setVal('cleaningPerStay', 40); setVal('platformFeePct', 3); setVal('utilitiesMonthly', 120);
  setVal('irpfMarginalRatePct', 30);
  setVal('rentInflationPct', 2.5); setVal('expenseInflationPct', 2.5); setVal('appreciationPct', 3);
  setVal('discountRatePct', 5); setVal('holdYears', 10);
  setVal('saleCommissionPct', 4); setVal('capitalGainsTaxPct', 21);
  toast('Ejemplo cargado: piso 75m² Alicante centro, 180k€, STR', 'success');
}

async function runProCalc(resultEl) {
  resultEl.innerHTML = '<div class="text-center py-8 text-ink-300"><div class="inline-block animate-pulse">⏳ Calculando 15+ métricas profesionales…</div></div>';
  const get = (k) => {
    const el = document.getElementById('r-' + k);
    if (!el) return undefined;
    const v = el.value;
    if (v === '' || v === null) return undefined;
    return el.type === 'number' ? Number(v) : v;
  };
  const getPct = (k) => { const v = get(k); return v !== undefined ? Number(v) / 100 : undefined; };
  const getCheck = (k) => document.getElementById('r-' + k)?.checked || false;

  const payload = {
    purchasePrice: get('purchasePrice'),
    marketValue: get('marketValue'),
    squareMeters: get('squareMeters'),
    bedrooms: get('bedrooms'),
    city: get('city'),
    propertyType: get('propertyType'),
    transferTaxPct: getPct('transferTaxPct'),
    notaryPct: getPct('notaryPct'),
    registryPct: getPct('registryPct'),
    legalFees: get('legalFees'),
    appraisalCost: get('appraisalCost'),
    brokerCommissionPct: getPct('brokerCommissionPct'),
    reformBudget: get('reformBudget'),
    contingencyPct: getPct('contingencyPct'),
    furnitureBudget: get('furnitureBudget'),
    appliancesBudget: get('appliancesBudget'),
    designBudget: get('designBudget'),
    downPaymentPct: getPct('downPaymentPct'),
    mortgageRateAnnualPct: getPct('mortgageRateAnnualPct'),
    mortgageYears: get('mortgageYears'),
    mortgageOpeningFeePct: getPct('mortgageOpeningFeePct'),
    bankFees: get('bankFees'),
    rentalMode: _calcState.rentalMode,
    monthlyRent: get('monthlyRent'),
    avgNightlyRate: get('avgNightlyRate'),
    occupancyPct: getPct('occupancyPct'),
    otherIncomeMonthly: get('otherIncomeMonthly'),
    avgNightsPerStay: get('avgNightsPerStay'),
    avgGuestsPerStay: get('avgGuestsPerStay'),
    hoaFeesMonthly: get('hoaFeesMonthly'),
    propertyTaxAnnual: get('propertyTaxAnnual'),
    garbageTaxAnnual: get('garbageTaxAnnual'),
    homeInsuranceAnnual: get('homeInsuranceAnnual'),
    rentDefaultInsuranceAnnual: get('rentDefaultInsuranceAnnual'),
    maintenancePctOfRent: getPct('maintenancePctOfRent'),
    vacancyPctExtra: getPct('vacancyPctExtra'),
    cleaningPerStay: get('cleaningPerStay'),
    cleaningStaysPerMonth: get('cleaningStaysPerMonth'),
    platformFeePct: getPct('platformFeePct'),
    managementFeePct: getPct('managementFeePct'),
    pricingSoftwareMonthly: get('pricingSoftwareMonthly'),
    touristTaxPerNightPerGuest: get('touristTaxPerNightPerGuest'),
    utilitiesMonthly: get('utilitiesMonthly'),
    irpfMarginalRatePct: getPct('irpfMarginalRatePct'),
    applyLtRentDeduction: getCheck('applyLtRentDeduction'),
    rentInflationPct: getPct('rentInflationPct'),
    expenseInflationPct: getPct('expenseInflationPct'),
    appreciationPct: getPct('appreciationPct'),
    discountRatePct: getPct('discountRatePct'),
    holdYears: get('holdYears'),
    saleCommissionPct: getPct('saleCommissionPct'),
    capitalGainsTaxPct: getPct('capitalGainsTaxPct'),
  };

  try {
    const r = await apiClient.post('/api/v1/tools/profitability-pro', payload);
    resultEl.innerHTML = '';
    resultEl.appendChild(renderProResult(r));
    resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (e) {
    resultEl.innerHTML = `<p class="text-red-300 text-sm mt-3 p-4 bg-red-500/10 border border-red-400/30 rounded-lg">⚠️ ${e?.message || 'error'}</p>`;
  }
}

// ============================================================================
// RESULTADO VISUAL (output rico)
// ============================================================================
function renderProResult(r) {
  const wrap = h('div', { class: 'space-y-5' });

  // === VEREDICTO BIG ===
  const v = r.verdict;
  const verdictColor = v.color === 'green' ? 'from-emerald-500/20 to-emerald-500/5 border-emerald-400'
    : v.color === 'yellow' ? 'from-amber-500/20 to-amber-500/5 border-amber-400'
    : 'from-red-500/20 to-red-500/5 border-red-400';
  wrap.appendChild(h('div', { class: `bg-gradient-to-br ${verdictColor} border-2 rounded-2xl p-5` }, [
    h('div', { class: 'flex items-start justify-between gap-4 flex-wrap' }, [
      h('div', {}, [
        h('h3', { class: 'font-display font-bold text-2xl mb-1' }, v.title),
        h('p', { class: 'text-sm text-ink-200' }, v.actionable),
      ]),
      h('div', { class: 'text-center' }, [
        h('div', { class: 'text-xs uppercase text-ink-400' }, 'Score'),
        h('div', { class: 'text-4xl font-display font-bold' }, `${v.score}/100`),
      ]),
    ]),
    h('div', { class: 'mt-3 space-y-1' }, v.reasons.map((reason) => h('div', { class: 'text-sm' }, reason))),
  ]));

  // === KPIs PRINCIPALES (con tooltips educativos) ===
  wrap.appendChild(h('div', {}, [
    h('div', { class: 'flex items-center gap-2 mb-3' }, [
      h('h4', { class: 'font-display font-bold text-lg' }, '📊 Métricas de rentabilidad'),
      h('span', { class: 'text-xs text-ink-400' }, '· pasa el ratón por encima para entender cada una'),
    ]),
    h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-2' }, [
      bigStat('Yield bruto', r.grossYieldPct + '%', 'gold',
        'Cuánto te da el piso AL AÑO en alquileres, dividido por el precio. Sin restar gastos. Buena referencia inicial: >5% bruto. Por debajo de 4% no merece la pena.'),
      bigStat('Yield neto', r.netYieldPct + '%', r.netYieldPct >= 6 ? 'green' : r.netYieldPct >= 4 ? 'gold' : 'red',
        'Lo mismo pero RESTANDO todos los gastos (comunidad, IBI, seguros, mantenimiento). Esta es la métrica que importa de verdad. 5-8% neto = buena inversión. <4% = invertir en otra cosa.'),
      bigStat('Cap Rate', r.capRatePct + '%', 'blue',
        'Como yield neto pero sobre el VALOR DE MERCADO actual (no el precio que pagaste). Útil si el piso se ha revalorizado. Indica la "rentabilidad pura" del inmueble como activo.'),
      bigStat('Cash-on-Cash', r.cashOnCashPct + '%', r.cashOnCashPct > 0 ? 'green' : 'red',
        'De cada euro REAL que sacaste del bolsillo (entrada + costes + reforma), cuánto te genera al año. Esta es la métrica clave si pides hipoteca. >8% excelente, 4-8% OK, <4% flojo.'),
      bigStat('IRR (TIR)', r.irrPct + '%', r.irrPct >= 10 ? 'green' : r.irrPct >= 6 ? 'gold' : 'red',
        'La rentabilidad REAL anualizada considerando TODO: alquileres + revalorización + amortización hipoteca + IRPF al vender. Es la métrica reina. Compárala con bolsa (S&P 500 ~10%/año). <8% mejor invierte en un fondo indexado.'),
      bigStat('ROI total año 1', r.totalROIYear1Pct + '%', 'gold',
        'Suma del primer año: cash flow + subida de valor del piso + reducción hipoteca. Dividido por tu cash invertido. Útil para ver el rendimiento total, no solo el alquiler.'),
      bigStat('NPV (' + r.inputsUsed.holdYears + 'a)', formatEur(r.npv), r.npv > 0 ? 'green' : 'red',
        'Valor Actual Neto. Si descuentas todos los flujos futuros a euros de hoy, ¿cuánto vale realmente esta inversión? POSITIVO = ganas dinero respecto a tu coste de oportunidad. NEGATIVO = pierdes.'),
      bigStat('Payback', r.paybackYears ? r.paybackYears + ' años' : '>' + r.inputsUsed.holdYears + 'a', 'ink',
        'En cuántos años recuperas el dinero que pusiste de tu bolsillo (sin contar la venta). Menos años = mejor. 8-12 años típico en España con hipoteca.'),
    ]),
  ]));

  // === RIESGO (con tooltips) ===
  wrap.appendChild(h('div', {}, [
    h('h4', { class: 'font-display font-bold text-lg mb-3' }, '⚠️ Métricas de riesgo'),
    h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-2' }, [
      bigStat('LTV', r.ltvPct + '%', r.ltvPct <= 70 ? 'green' : r.ltvPct <= 80 ? 'gold' : 'red',
        'Loan-to-Value: % del piso que pides al banco. 70% LTV = pones 30% entrada. Bancos en España dan hasta 80% en 2ª vivienda. LTV alto = más riesgo pero más apalancamiento (potencial mayor retorno).'),
      bigStat('DSCR', r.dscr + '×', r.dscr >= 1.4 ? 'green' : r.dscr >= 1.2 ? 'gold' : 'red',
        'Debt Service Coverage Ratio: cuántas veces los ingresos del piso cubren la cuota de hipoteca. 1.5× = sobra 50%. >1.25× es seguro. <1× = el piso NO se paga solo, tienes que poner dinero cada mes.'),
      bigStat('Breakeven ocupación', r.breakevenOccupancyPct + '%', 'ink',
        '(Solo turístico) Qué % de ocupación necesitas MÍNIMO para no perder dinero. Si tu mercado típico da 70% y este número es 80%, problema gordo. Si es 55%, vas con colchón.'),
      bigStat('Debt Yield', r.debtYieldPct + '%', 'ink',
        'NOI ÷ Préstamo. Lo que mira el banco para decidir si te da hipoteca. >10% seguro para banco. <8% los bancos se ponen nerviosos. Métrica de banca comercial.'),
    ]),
  ]));

  // === INVERSIÓN INICIAL ===
  wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('h4', { class: 'font-display font-bold text-base mb-3' }, '💰 Inversión inicial requerida'),
    h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-3' }, [
      miniInfoStat('Coste total adquisición', formatEur(r.totalAcquisitionCost)),
      miniInfoStat('Préstamo solicitado', formatEur(r.loanAmount)),
      miniInfoStat('Entrada', formatEur(r.downPayment)),
      miniInfoStat('💸 CASH NECESARIO', formatEur(r.totalCashInvested), 'gold'),
    ]),
    h('details', { class: 'mt-2' }, [
      h('summary', { class: 'text-xs text-gold-300 cursor-pointer' }, 'Ver desglose de costes'),
      h('table', { class: 'w-full text-xs mt-2' }, Object.entries(r.acquisitionCostBreakdown).map(([k, v]) => h('tr', {}, [
        h('td', { class: 'py-1 text-ink-300' }, prettyKey(k)),
        h('td', { class: 'py-1 text-right text-gold-300' }, formatEur(v)),
      ]))),
    ]),
  ]));

  // === CASH FLOW AÑO 1 ===
  wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('h4', { class: 'font-display font-bold text-base mb-3' }, '💵 Cash flow año 1'),
    h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, [
      miniInfoStat('Ingresos brutos', formatEur(r.grossAnnualIncome)),
      miniInfoStat('Gastos operativos', formatEur(r.totalOperatingExpensesYear1), 'red'),
      miniInfoStat('NOI (Net Operating)', formatEur(r.noiYear1), 'gold'),
      miniInfoStat('Cuota hipoteca/mes', formatEur(r.monthlyDebtPayment), 'red'),
      miniInfoStat('Servicio deuda año', formatEur(r.annualDebtService), 'red'),
      miniInfoStat('Cash flow pre-impuestos', formatEur(r.cashFlowYear1), r.cashFlowYear1 > 0 ? 'green' : 'red'),
      miniInfoStat('Impuestos año 1', formatEur(r.taxesYear1), 'red'),
      miniInfoStat('💰 CASH FLOW NETO/AÑO', formatEur(r.cashFlowAfterTaxYear1), r.cashFlowAfterTaxYear1 > 0 ? 'green' : 'red'),
    ]),
    h('div', { class: 'mt-3 text-sm text-center' }, [
      h('span', { class: 'text-ink-400' }, 'Cash flow MENSUAL neto: '),
      h('span', { class: `text-xl font-display font-bold ${(r.cashFlowAfterTaxYear1 / 12) >= 0 ? 'text-emerald-300' : 'text-red-300'}` },
        `${(r.cashFlowAfterTaxYear1 / 12) >= 0 ? '+' : ''}${formatEur(r.cashFlowAfterTaxYear1 / 12)}/mes`),
    ]),
  ]));

  // === REGLAS RÁPIDAS ===
  wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('h4', { class: 'font-display font-bold text-base mb-3' }, '⚡ Reglas rápidas del inversor (USA)'),
    h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, [
      ruleBadge('1% Rule', `${r.onePctRule.ratio}%`, r.onePctRule.passed, 'Alquiler ≥ 1% precio'),
      ruleBadge('2% Rule', `${r.twoPctRule.ratio}%`, r.twoPctRule.passed, 'Gran inversión si ≥2%'),
      ruleBadge('50% Rule', `${r.fiftyPctRule.opexRatio}%`, r.fiftyPctRule.passed, 'OpEx ≤50% ingresos'),
      miniInfoStat('GRM (multiplicador)', r.grm + '×', r.grm < 15 ? 'green' : r.grm < 20 ? 'gold' : 'red'),
    ]),
  ]));

  // === STRESS TESTS ===
  wrap.appendChild(h('div', { class: 'bg-red-500/5 border border-red-400/20 rounded-xl p-4' }, [
    h('h4', { class: 'font-display font-bold text-base mb-3' }, '🚨 Stress tests — ¿sobrevive a escenarios malos?'),
    h('div', { class: 'space-y-2' }, [
      stressRow('📉 Caída de renta -20%', r.stressTests.rentDrop20Pct),
      stressRow('📈 Subida de tipos +2pp', r.stressTests.rateRise2Pts),
      stressRow('🌧 Caída ocupación -30%', r.stressTests.occupancyDrop30Pct),
      stressRow('☠️ Combo peor caso', r.stressTests.worstCaseCombined),
    ]),
  ]));

  // === COMPARATIVA LT vs ST ===
  if (r.longTermAlternative || r.shortTermAlternative) {
    wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h4', { class: 'font-display font-bold text-base mb-3' }, '⚖️ Comparativa Long-term vs Short-term'),
      h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-3' }, [
        r.longTermAlternative ? h('div', { class: 'bg-base-900/40 rounded-lg p-3 border border-base-700' }, [
          h('div', { class: 'font-semibold mb-2' }, '📅 Long-term (>1 año)'),
          h('div', { class: 'text-sm' }, `Renta estimada: ${formatEur(r.longTermAlternative.monthlyRentEstimate)}/mes`),
          h('div', { class: 'text-sm' }, `Yield neto: ${r.longTermAlternative.yieldNetPct}%`),
          h('div', { class: 'text-sm' }, `Cash flow: ${formatEur(r.longTermAlternative.cashFlowMonthly)}/mes`),
        ]) : null,
        r.shortTermAlternative ? h('div', { class: 'bg-base-900/40 rounded-lg p-3 border border-base-700' }, [
          h('div', { class: 'font-semibold mb-2' }, '✈️ Short-term (Airbnb)'),
          h('div', { class: 'text-sm' }, `Ingreso medio: ${formatEur(r.shortTermAlternative.monthlyRevenueEstimate)}/mes`),
          h('div', { class: 'text-sm' }, `Yield neto: ${r.shortTermAlternative.yieldNetPct}%`),
          h('div', { class: 'text-sm' }, `Cash flow: ${formatEur(r.shortTermAlternative.cashFlowMonthly)}/mes`),
        ]) : null,
      ]),
    ]));
  }

  // === CASH FLOW PROYECTADO TABLA ===
  wrap.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('h4', { class: 'font-display font-bold text-base mb-3' }, `📅 Cash flow proyectado a ${r.inputsUsed.holdYears} años`),
    h('div', { class: 'overflow-x-auto' }, [
      h('table', { class: 'w-full text-xs' }, [
        h('thead', {}, [
          h('tr', { class: 'border-b border-base-700' }, [
            h('th', { class: 'text-left py-2 px-2' }, 'Año'),
            h('th', { class: 'text-right py-2 px-2' }, 'Ingresos'),
            h('th', { class: 'text-right py-2 px-2' }, 'OpEx'),
            h('th', { class: 'text-right py-2 px-2' }, 'NOI'),
            h('th', { class: 'text-right py-2 px-2' }, 'Hipoteca'),
            h('th', { class: 'text-right py-2 px-2' }, 'Impuestos'),
            h('th', { class: 'text-right py-2 px-2' }, 'CF neto'),
            h('th', { class: 'text-right py-2 px-2' }, 'Valor inmueble'),
            h('th', { class: 'text-right py-2 px-2' }, 'Equity'),
          ]),
        ]),
        h('tbody', {}, r.cashFlows.map((cf) => h('tr', { class: 'border-b border-base-700/40 hover:bg-base-700/20' }, [
          h('td', { class: 'py-1.5 px-2 font-medium' }, String(cf.year)),
          h('td', { class: 'py-1.5 px-2 text-right' }, formatEur(cf.effectiveIncome)),
          h('td', { class: 'py-1.5 px-2 text-right text-red-300' }, formatEur(-cf.operatingExpenses)),
          h('td', { class: 'py-1.5 px-2 text-right text-gold-300' }, formatEur(cf.noi)),
          h('td', { class: 'py-1.5 px-2 text-right text-red-300' }, formatEur(-cf.debtService)),
          h('td', { class: 'py-1.5 px-2 text-right text-red-300' }, formatEur(-cf.taxes)),
          h('td', { class: `py-1.5 px-2 text-right font-bold ${cf.cashFlowAfterTax >= 0 ? 'text-emerald-300' : 'text-red-300'}` }, formatEur(cf.cashFlowAfterTax)),
          h('td', { class: 'py-1.5 px-2 text-right' }, formatEur(cf.propertyValue)),
          h('td', { class: 'py-1.5 px-2 text-right text-gold-300 font-semibold' }, formatEur(cf.equityValue)),
        ]))),
      ]),
    ]),
    h('div', { class: 'mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-base-700' }, [
      miniInfoStat('Cash flow total ' + r.inputsUsed.holdYears + 'a', formatEur(r.totalCashFlowOverHold)),
      miniInfoStat('Equity final', formatEur(r.finalEquityValue), 'gold'),
      miniInfoStat('Neto si vendes', formatEur(r.netSaleProceeds), 'gold'),
      miniInfoStat('Retorno total', formatEur(r.totalReturnAtSale), r.totalReturnAtSale > 0 ? 'green' : 'red'),
    ]),
  ]));

  return wrap;
}

function bigStat(label, value, color = 'gold', tooltip) {
  const colors = { gold: 'text-gold-300', green: 'text-emerald-300', red: 'text-red-300', blue: 'text-sky-300', ink: 'text-ink-100' };
  return h('div', {
    class: 'bg-base-800/50 border border-base-700 rounded-lg p-3 text-center group relative cursor-help',
    title: tooltip || '',
  }, [
    h('div', { class: 'text-[10px] uppercase tracking-wider text-ink-400 mb-1 flex items-center justify-center gap-1' }, [
      h('span', {}, label),
      tooltip ? h('span', { class: 'text-ink-500 text-[9px] opacity-60 group-hover:opacity-100' }, '(?)') : null,
    ]),
    h('div', { class: `text-xl font-display font-bold ${colors[color] || colors.gold}` }, value),
    tooltip ? h('div', {
      class: 'absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-base-900 border border-gold-400/40 rounded-lg p-3 text-xs text-ink-200 leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition shadow-2xl pointer-events-none',
    }, tooltip) : null,
  ]);
}

function miniInfoStat(label, value, color = 'ink') {
  const colors = { gold: 'text-gold-300', green: 'text-emerald-300', red: 'text-red-300', ink: 'text-ink-100', blue: 'text-sky-300' };
  return h('div', { class: 'bg-base-900/40 rounded-lg p-2.5 border border-base-700' }, [
    h('div', { class: 'text-[10px] uppercase text-ink-400 tracking-wider' }, label),
    h('div', { class: `text-base font-display font-bold ${colors[color] || colors.ink}` }, value),
  ]);
}

function ruleBadge(name, value, passed, desc) {
  return h('div', { class: `rounded-lg p-3 border text-center ${passed ? 'bg-emerald-500/10 border-emerald-400/40' : 'bg-base-900/40 border-base-700'}` }, [
    h('div', { class: 'text-[10px] uppercase text-ink-400' }, name),
    h('div', { class: `text-lg font-display font-bold ${passed ? 'text-emerald-300' : 'text-ink-300'}` }, value + (passed ? ' ✓' : '')),
    h('div', { class: 'text-[9px] text-ink-500 mt-0.5' }, desc),
  ]);
}

function stressRow(label, data) {
  const color = data.verdict.startsWith('✅') ? 'text-emerald-300' : data.verdict.startsWith('⚠️') ? 'text-amber-300' : 'text-red-300';
  return h('div', { class: 'grid grid-cols-3 md:grid-cols-4 gap-3 items-center text-sm py-2 border-b border-base-700/40 last:border-0' }, [
    h('div', { class: 'font-medium' }, label),
    h('div', { class: `text-right md:text-center ${data.cashFlowMonthly >= 0 ? 'text-emerald-300' : 'text-red-300'}` }, `${formatEur(data.cashFlowMonthly)}/mes`),
    h('div', { class: 'text-right md:text-center text-ink-300' }, `DSCR ${data.dscr}×`),
    h('div', { class: `text-right font-semibold ${color}` }, data.verdict),
  ]);
}

function prettyKey(k) {
  const map = {
    purchasePrice: 'Precio compra', transferTax: 'ITP/IVA', notary: 'Notaría', registry: 'Registro',
    legalFees: 'Gestoría', appraisalCost: 'Tasación', brokerCommission: 'Comisión agencia',
    reformWithContingency: 'Reforma + imprevistos', furniture: 'Muebles', appliances: 'Electrodomésticos',
    design: 'Diseño', bankFees: 'Costes banco', mortgageOpeningFee: 'Comisión apertura hipoteca',
  };
  return map[k] || k;
}

// ============================================================================
// GLOSARIO EXPANDIBLE — explica TODAS las métricas en lenguaje claro
// ============================================================================
function buildGlossaryPanel() {
  const wrap = h('details', { class: 'bg-base-800/40 border border-base-700 rounded-xl overflow-hidden mb-4' });
  wrap.appendChild(h('summary', {
    class: 'px-4 py-3 cursor-pointer font-display font-semibold text-base hover:bg-base-800/60 select-none flex items-center gap-2',
  }, [
    h('span', {}, '📖'),
    h('span', {}, '¿Qué significa cada palabreja? · Glosario completo'),
    h('span', { class: 'text-xs text-ink-400 ml-auto' }, '(haz click para abrir)'),
  ]));

  const body = h('div', { class: 'p-5 border-t border-base-700 space-y-4 text-sm' });

  const term = (name, simple, detail) => h('div', { class: 'pb-3 border-b border-base-700/40 last:border-0' }, [
    h('div', { class: 'flex items-baseline gap-2 mb-1 flex-wrap' }, [
      h('span', { class: 'font-display font-bold text-gold-300' }, name),
      h('span', { class: 'text-ink-100 text-sm' }, '· ' + simple),
    ]),
    detail ? h('p', { class: 'text-xs text-ink-400 leading-relaxed pl-2' }, detail) : null,
  ]);

  body.appendChild(h('h5', { class: 'font-display font-semibold text-gold-200 mt-2 mb-2' }, '📊 RENTABILIDAD'));
  body.appendChild(term('Yield bruto',
    'Cuánto te da el piso AL AÑO en alquileres, dividido por el precio. Sin restar nada.',
    'Ejemplo: piso de 100.000€ que da 500€/mes (6.000€/año) → 6% bruto. Es la primera mirada. Si es <4% el piso no compensa.'));
  body.appendChild(term('Yield neto',
    'Lo mismo pero RESTANDO gastos (comunidad, IBI, seguros, mantenimiento, etc.).',
    'Es el que importa de verdad. Buenas inversiones: 5-8% neto. Por debajo de 4% mejor invertir en otra cosa.'));
  body.appendChild(term('Cap Rate',
    'Yield neto pero comparando con el VALOR DE MERCADO actual (no el precio que pagaste).',
    'Útil cuando el piso se ha revalorizado. Si compraste por 100k y ahora vale 150k, tu Cap Rate baja porque "podrías sacar más vendiéndolo y poniendo el dinero en otra parte".'));
  body.appendChild(term('Cash-on-Cash',
    'De cada euro REAL que sacaste del bolsillo, cuánto te genera el piso cada año.',
    'Ejemplo: pusiste 50.000€ de entrada y el piso te da 4.000€/año netos → 8% cash-on-cash. Esta es la métrica clave si pides hipoteca.'));
  body.appendChild(term('IRR (TIR · Tasa Interna de Retorno)',
    'La rentabilidad REAL anualizada considerando todo: alquileres + revalorización del piso + amortización hipoteca + impuestos al vender.',
    'Es la métrica reina. Compara con bolsa (S&P 500 hace ~10%/año). Si tu IRR es <8% mejor mete el dinero en un fondo indexado.'));
  body.appendChild(term('NPV (Valor Actual Neto)',
    'Si descuentas todos los flujos futuros a euros de hoy, ¿cuánto vale realmente esta inversión?',
    'Positivo = ganas dinero respecto a tu coste de oportunidad. Negativo = pierdes.'));
  body.appendChild(term('Payback',
    'En cuántos años recuperas el dinero que pusiste de tu bolsillo (sin contar la venta).',
    'Menos años = mejor. 8-12 años es típico en España con hipoteca.'));
  body.appendChild(term('ROI total año 1',
    'Suma: cash flow del año + lo que sube el piso de valor + lo que reduces de hipoteca. Todo dividido por tu cash invertido.',
    'Por qué importa: aunque el alquiler dé poco, si el piso se aprecia +3%/año, ganas igualmente.'));

  body.appendChild(h('h5', { class: 'font-display font-semibold text-gold-200 mt-4 mb-2' }, '⚠️ RIESGO'));
  body.appendChild(term('LTV (Loan-to-Value)',
    'Porcentaje del piso que pides al banco. 70% LTV = pones 30% entrada.',
    'Bancos en España dan hasta 80% en 2ª vivienda (inversión). LTV alto = más riesgo pero más apalancamiento.'));
  body.appendChild(term('DSCR (Debt Service Coverage Ratio)',
    'Cuántas veces los ingresos del piso cubren la cuota de hipoteca.',
    'DSCR 1.5× = los ingresos cubren 1,5 veces la cuota (sobra 50%). >1.25× es seguro. <1× = el piso NO se paga solo, tienes que poner dinero cada mes.'));
  body.appendChild(term('Breakeven occupancy',
    '(Solo turístico) Qué % de ocupación necesitas mínimo para no perder dinero.',
    'Si es 65% y la ocupación realista es 70%, vas justos. Si es 80% y tu mercado da 65%, problema gordo.'));
  body.appendChild(term('Debt Yield',
    'NOI ÷ Préstamo. Lo que mira el banco para decidir si te presta.',
    '>10% es seguro para banco. <8% se ponen nerviosos. Métrica de banca comercial.'));
  body.appendChild(term('NOI (Net Operating Income)',
    'Ingresos del piso MENOS gastos operativos (sin contar hipoteca ni impuestos).',
    'Es lo que da el inmueble "puro" antes de financiación. Si pagas al contado, el NOI es tu beneficio bruto.'));

  body.appendChild(h('h5', { class: 'font-display font-semibold text-gold-200 mt-4 mb-2' }, '⚡ REGLAS RÁPIDAS DEL INVERSOR'));
  body.appendChild(term('1% Rule',
    'El alquiler MENSUAL debería ser al menos el 1% del precio compra.',
    'Piso de 100k → 1.000€/mes. Si lo pasa → buena inversión. En España es difícil pasarla salvo en regiones baratas, así que se usa como referencia, no como obligación.'));
  body.appendChild(term('2% Rule',
    'Si pasa el 2% (200€/mes por cada 10k de precio) → inversión EXCELENTE.',
    'Muy raro en España, más común en zonas industriales USA.'));
  body.appendChild(term('50% Rule',
    'Los gastos operativos suelen ser ~50% de los ingresos brutos.',
    'Si tu cálculo da OpEx muy por debajo del 50%, probablemente te has olvidado de algo. Si pasa de 50%, el piso es caro de mantener.'));
  body.appendChild(term('GRM (Gross Rent Multiplier)',
    'Cuántas veces el alquiler anual cuesta el piso.',
    'GRM 12 = el piso vale 12 años de alquiler. Cuanto menor mejor. <15 es bueno en España.'));

  body.appendChild(h('h5', { class: 'font-display font-semibold text-gold-200 mt-4 mb-2' }, '🚨 STRESS TESTS'));
  body.appendChild(term('Caída renta -20%',
    '¿Y si baja el alquiler 20% por crisis? ¿Sigue siendo rentable?',
    'Si DSCR queda >1 y cash flow positivo → sobrevive. Si no, mejor pasar.'));
  body.appendChild(term('Subida tipos +2pp',
    '¿Y si el Euríbor sube 2 puntos? (escenario tipo 2022-2023)',
    'Tu cuota hipoteca sube y come tu cash flow. Crítico si tienes hipoteca variable.'));
  body.appendChild(term('Caída ocupación -30%',
    '(Turístico) ¿Y si baja la ocupación de 70% a 49%?',
    'Test importante en zonas saturadas. Si no aguanta esto, riesgo alto.'));
  body.appendChild(term('Combo peor caso',
    'Los 3 stress tests a la vez (suavizados). Catástrofe realista.',
    'Si sobrevives a esto, el piso es ULTRA seguro.'));

  body.appendChild(h('h5', { class: 'font-display font-semibold text-gold-200 mt-4 mb-2' }, '🇪🇸 COSTES ESPAÑA (te lo explico una vez)'));
  body.appendChild(term('ITP (Impuesto Transmisiones Patrimoniales)',
    'Lo que pagas a Hacienda cuando compras una vivienda DE SEGUNDA MANO.',
    'En la Comunidad Valenciana es 10% del precio. En Madrid 6%. Si es obra nueva pagas IVA 10% en lugar de ITP.'));
  body.appendChild(term('Notaría + Registro + Gestoría',
    'Costes administrativos obligatorios al comprar.',
    'Notaría ~0.5%, Registro ~0.3%, Gestoría ~400€. Total típico ~1.500€ en piso 100k€.'));
  body.appendChild(term('Comisión apertura hipoteca',
    'Lo que cobra el banco por darte la hipoteca.',
    '0-1% típico. Algunos bancos la quitan a cambio de vincularte (seguros, plan pensiones).'));
  body.appendChild(term('Deducción -60% (alquiler LT)',
    'Si alquilas a alguien que la usa como vivienda habitual (largo plazo), descuentas el 60% del rendimiento neto en IRPF.',
    'NO aplica a alquiler turístico. Solo LT con contrato LAU vivienda habitual.'));
  body.appendChild(term('Tasa turística',
    'Algunos sitios cobran un impuesto por noche/huésped en alquiler vacacional.',
    'Baleares cobra 1-2€/noche. CV pronto. Si tu mercado la tiene, ponla.'));

  wrap.appendChild(body);
  return wrap;
}

// ============================== TAB 1: Mis inmuebles ==============================
async function renderMyProperties(container) {
  container.innerHTML = '';

  // Quick links panel — enlaces directos a cuentas/plataformas (Idealista, Fotocasa, Airbnb, PriceLabs, etc.)
  try {
    const { renderQuickLinks } = await import('./_quick-links.js');
    await renderQuickLinks(container, 'inmuebles', {
      title: '🔗 Mis cuentas inmobiliarias',
      subtitle: 'Acceso directo a tus cuentas Idealista, Fotocasa, Airbnb, PriceLabs, etc.',
      onReload: () => renderMyProperties(container),
    });
  } catch (e) {
    console.warn('Quick links no disponibles:', e);
  }

  const [propsRes, aggRes] = await Promise.all([
    apiClient.get('/api/v1/properties').catch(() => ({ data: [] })),
    apiClient.get('/api/v1/properties/aggregate-stats').catch(() => null),
  ]);
  const props = propsRes.data || [];

  // Resumen global arriba
  if (aggRes) {
    const stats = h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-3 mb-5' }, [
      bigKpi('Inmuebles', aggRes.totalProperties || 0, '🏠', 'gold'),
      bigKpi('Reservas', aggRes.totalBookings || 0, '📅', 'blue'),
      bigKpi('Noches '+new Date().getFullYear(), aggRes.totalNightsThisYear || 0, '🌙', 'purple'),
      bigKpi('Ocupación med.', `${aggRes.avgOccupancy || 0}%`, '📊', 'green'),
      bigKpi('Ingresos est.', formatEur(aggRes.totalEstimatedRevenue || 0), '💰', 'gold'),
    ]);
    container.appendChild(stats);
  }

  if (props.length === 0) {
    container.appendChild(emptyState('🏠', 'Sin inmuebles aún', 'Añade tu primer inmueble con el botón de arriba.'));
    return;
  }

  // ========================================================================
  // RESPETAR ESTRUCTURA PADRE/HIJOS:
  //   • Padres (sin parentId): cards grandes en grid
  //   • Hijos (con parentId): se renderizan ANIDADOS dentro de su padre
  //   • Huérfanos (sin parentId Y sin hijos): cards normales como antes
  // ========================================================================
  const perProperty = (aggRes?.perProperty || []).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
  const parents = props.filter((p) => !p.parentId);
  const childrenByParent = props.filter((p) => p.parentId).reduce((acc, p) => {
    if (!acc[p.parentId]) acc[p.parentId] = [];
    acc[p.parentId].push(p);
    return acc;
  }, {});

  // Renderizar cada padre como bloque con sus hijos dentro
  parents.forEach((parent) => {
    const children = childrenByParent[parent.id] || [];
    if (children.length > 0) {
      // Padre con hijos → bloque jerárquico
      container.appendChild(propGroupCard(parent, children, perProperty, container));
    } else {
      // Sin hijos → card normal
      const solo = h('div', { class: 'mb-4' });
      solo.appendChild(propCard(parent, perProperty[parent.id], container));
      container.appendChild(solo);
    }
  });

  // Hijos huérfanos (con parentId pero el padre no existe) — fallback
  const orphanChildren = props.filter((p) => p.parentId && !parents.find((pp) => pp.id === p.parentId));
  if (orphanChildren.length > 0) {
    const orphanGrid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4' });
    orphanChildren.forEach((p) => orphanGrid.appendChild(propCard(p, perProperty[p.id], container)));
    container.appendChild(orphanGrid);
  }

  // === ARCHIVOS IMPORTANTES (debajo del piso) ===
  try {
    const docContainer = h('div', { class: 'mt-5' });
    container.appendChild(docContainer);
    await renderImportantDocs(docContainer);
  } catch (e) { console.warn('Docs failed:', e); }
}

// ============================================================================
// CARD DE GRUPO: 1 inmueble padre con N anuncios (hijos) dentro.
// Ejemplo: Vázquez Mella N20 → Estudio 1, 2, 3 + Anuncio 4
// ============================================================================
function propGroupCard(parent, children, perPropertyMap, container) {
  const parentStats = perPropertyMap[parent.id] || {};
  // Agregar stats de los hijos (lo que cuenta para Carlos)
  const totalRevenue = children.reduce((s, c) => s + ((perPropertyMap[c.id] || {}).estimatedRevenue || 0), 0);
  const totalBookings = children.reduce((s, c) => s + ((perPropertyMap[c.id] || {}).upcomingCount || 0), 0);
  const avgOccupancy = children.length > 0
    ? Math.round(children.reduce((s, c) => s + ((perPropertyMap[c.id] || {}).occupancyPct || 0), 0) / children.length)
    : 0;

  // Última sync: el más reciente updated_at de los hijos
  const lastSyncMs = Math.max(0, ...children.map((c) => c.updatedAt ? new Date(c.updatedAt).getTime() : 0));
  const syncAgo = lastSyncMs > 0 ? agoLabel(lastSyncMs) : 'sin datos';

  const wrap = h('div', { class: 'mb-5 bg-gradient-to-br from-gold-500/5 to-transparent border border-gold-400/30 rounded-2xl overflow-hidden' });

  // HEADER del padre
  wrap.appendChild(h('div', {
    class: 'p-5 border-b border-gold-400/20 cursor-pointer hover:bg-gold-500/5 transition',
    onclick: () => openPropertyDetail(parent, container),
  }, [
    h('div', { class: 'flex items-start justify-between gap-3 mb-3' }, [
      h('div', { class: 'flex items-center gap-3 min-w-0' }, [
        h('div', { class: 'w-14 h-14 rounded-xl bg-gold-500/20 border border-gold-400/40 flex items-center justify-center text-2xl shrink-0' }, '🏠'),
        h('div', { class: 'min-w-0' }, [
          h('h3', { class: 'font-display font-bold text-lg leading-tight truncate' }, parent.name),
          h('p', { class: 'text-xs text-ink-300' }, parent.address || parent.city || 'Alicante'),
          h('p', { class: 'text-xs text-gold-300 mt-1' }, `${children.length} ${children.length === 1 ? 'anuncio' : 'anuncios'} Airbnb · iCal ${syncAgo}`),
        ]),
      ]),
      h('div', { class: 'text-right shrink-0' }, [
        h('div', { class: 'text-xs uppercase tracking-wider text-ink-400' }, 'Próximas'),
        h('div', { class: 'text-2xl font-display font-bold text-gold-300' }, String(totalBookings)),
      ]),
    ]),
    // Mini-KPIs del grupo
    h('div', { class: 'grid grid-cols-3 gap-2 text-center text-xs' }, [
      h('div', { class: 'bg-base-900/40 rounded-lg p-2' }, [
        h('div', { class: 'text-ink-400' }, 'Ingresos estimados'),
        h('div', { class: 'text-base font-display font-bold text-gold-300' }, formatEur(totalRevenue)),
      ]),
      h('div', { class: 'bg-base-900/40 rounded-lg p-2' }, [
        h('div', { class: 'text-ink-400' }, 'Ocupación med.'),
        h('div', { class: 'text-base font-display font-bold text-emerald-300' }, `${avgOccupancy}%`),
      ]),
      h('div', { class: 'bg-base-900/40 rounded-lg p-2' }, [
        h('div', { class: 'text-ink-400' }, 'Reservas próx'),
        h('div', { class: 'text-base font-display font-bold text-blue-300' }, String(totalBookings)),
      ]),
    ]),
  ]));

  // LISTA DE ANUNCIOS (hijos)
  const childrenList = h('div', { class: 'divide-y divide-gold-400/10' });
  // Orden: primero el "Anuncio 4" o anuncios especiales arriba, luego estudios 1-2-3
  const sortedChildren = [...children].sort((a, b) => {
    const aIsSpecial = /anuncio.*4|min.*noche/i.test(a.name);
    const bIsSpecial = /anuncio.*4|min.*noche/i.test(b.name);
    if (aIsSpecial && !bIsSpecial) return -1;
    if (!aIsSpecial && bIsSpecial) return 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  sortedChildren.forEach((child) => {
    const stats = perPropertyMap[child.id] || {};
    const childSyncAgo = child.updatedAt ? agoLabel(new Date(child.updatedAt).getTime()) : '—';
    const isSpecial = /anuncio.*4|min.*noche/i.test(child.name);
    const shortName = child.name.replace(/^.*[—–-]\s*/, '').trim();

    childrenList.appendChild(h('div', {
      class: 'p-4 hover:bg-base-800/40 cursor-pointer transition',
      onclick: () => openPropertyDetail(child, container),
    }, [
      h('div', { class: 'flex items-center justify-between gap-3 flex-wrap' }, [
        h('div', { class: 'flex items-center gap-3 min-w-0' }, [
          h('div', { class: `w-10 h-10 rounded-lg flex items-center justify-center text-lg shrink-0 ${isSpecial ? 'bg-purple-500/20 border border-purple-400/30' : 'bg-blue-500/20 border border-blue-400/30'}` }, isSpecial ? '📅' : '🛏'),
          h('div', { class: 'min-w-0' }, [
            h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
              h('span', { class: 'font-semibold text-sm' }, shortName),
              isSpecial ? pill('mín 3 noches', 'purple') : pill('mín 10 noches', 'blue'),
              child.icalUrl ? h('span', { class: 'text-[10px] text-emerald-300' }, '✓ iCal') : h('span', { class: 'text-[10px] text-amber-300' }, '⚠ sin iCal'),
            ]),
            h('div', { class: 'text-[10px] text-ink-400 mt-0.5' }, `Actualizado ${childSyncAgo}`),
          ]),
        ]),
        h('div', { class: 'flex gap-4 text-xs items-center' }, [
          h('div', { class: 'text-center' }, [
            h('div', { class: 'text-ink-400' }, 'Próx'),
            h('div', { class: 'text-blue-300 font-bold' }, String(stats.upcomingCount || 0)),
          ]),
          h('div', { class: 'text-center' }, [
            h('div', { class: 'text-ink-400' }, 'Ocup'),
            h('div', { class: 'text-emerald-300 font-bold' }, `${stats.occupancyPct || 0}%`),
          ]),
          h('div', { class: 'text-center' }, [
            h('div', { class: 'text-ink-400' }, 'Ingresos'),
            h('div', { class: 'text-gold-300 font-bold' }, formatEur(stats.estimatedRevenue || 0)),
          ]),
          h('div', { class: 'text-ink-400 group-hover:text-gold-300' }, '›'),
        ]),
      ]),
    ]));
  });
  wrap.appendChild(childrenList);

  // FOOTER con botón sync manual
  wrap.appendChild(h('div', { class: 'px-4 py-2 bg-base-900/40 border-t border-gold-400/10 flex items-center justify-between text-xs' }, [
    h('span', { class: 'text-ink-400' }, '🔄 Auto-sync iCal cada 30 min · próx en breve'),
    h('button', {
      class: 'text-gold-300 hover:text-gold-200 underline',
      onclick: async (e) => {
        e.stopPropagation();
        e.target.textContent = 'Sincronizando…';
        try {
          await apiClient.post('/api/v1/properties/sync-ical', {});
          toast('iCals actualizados', 'success');
          renderMyProperties(container);
        } catch (err) {
          toast('Error sync: ' + (err?.message || ''), 'error');
          e.target.textContent = 'Sincronizar ahora';
        }
      },
    }, 'Sincronizar ahora'),
  ]));

  return wrap;
}

// Helper: "hace X min" / "hace X h" / "hace X días"
function agoLabel(timestampMs) {
  if (!timestampMs) return 'sin datos';
  const diff = Date.now() - timestampMs;
  if (diff < 60 * 1000) return 'hace segundos';
  if (diff < 60 * 60 * 1000) return `hace ${Math.floor(diff / 60000)} min`;
  if (diff < 24 * 60 * 60 * 1000) return `hace ${Math.floor(diff / 3600000)} h`;
  return `hace ${Math.floor(diff / 86400000)} días`;
}

function bigKpi(label, value, emoji, color) {
  const cols = {
    gold: 'border-gold-400/30 from-gold-500/10', blue: 'border-sky-400/30 from-sky-500/10',
    green: 'border-emerald-400/30 from-emerald-500/10', red: 'border-red-400/30 from-red-500/10',
    purple: 'border-purple-400/30 from-purple-500/10',
  };
  const vc = { gold: 'text-gold-300', blue: 'text-sky-300', green: 'text-emerald-300', red: 'text-red-300', purple: 'text-purple-300' };
  return h('div', { class: `bg-gradient-to-br ${cols[color] || cols.gold} to-transparent bg-base-800/40 border rounded-xl p-3` }, [
    h('div', { class: 'flex items-center justify-between mb-1' }, [
      h('span', { class: 'text-xs uppercase tracking-wider text-ink-400 font-semibold' }, label),
      h('span', { class: 'text-base' }, emoji),
    ]),
    h('div', { class: `text-2xl font-display font-bold ${vc[color] || 'text-ink-100'}` }, String(value)),
  ]);
}

function propCard(p, stats, container) {
  // Placeholder visual: avatar circular con primera letra + color hash del nombre
  const colorHash = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = ((h << 5) - h) + str.charCodeAt(i);
    const hue = Math.abs(h) % 360;
    return `hsl(${hue}, 65%, 25%)`;
  };
  const initial = (p.name || '?').replace(/^.*[—-]\s*/, '').charAt(0).toUpperCase() || (p.name || '?').charAt(0).toUpperCase();

  const card = h('div', {
    class: 'bg-base-800/50 border border-base-700 rounded-xl p-5 hover:border-gold-400/40 cursor-pointer transition group',
    onclick: () => openPropertyDetail(p, container),
  }, [
    h('div', { class: 'flex items-start gap-3 mb-3' }, [
      p.photoUrl
        ? h('img', { src: p.photoUrl, class: 'w-12 h-12 rounded-xl object-cover', alt: p.name })
        : h('div', {
            class: 'w-12 h-12 rounded-xl flex items-center justify-center font-display font-bold text-xl text-white shrink-0',
            style: `background: ${colorHash(p.name || '')}`,
          }, initial),
      h('div', { class: 'flex-1 min-w-0' }, [
        h('div', { class: 'flex items-start justify-between gap-2' }, [
          h('h4', { class: 'font-display font-semibold leading-tight truncate' }, p.name),
          pill(p.operationMode || '—', 'gold'),
        ]),
        h('p', { class: 'text-xs text-ink-300' }, p.address || p.city || '—'),
      ]),
    ]),
    stats ? h('div', { class: 'grid grid-cols-3 gap-2 my-3 text-center text-xs' }, [
      h('div', { class: 'bg-base-900/40 rounded p-2' }, [
        h('div', { class: 'text-ink-400' }, 'Reservas'),
        h('div', { class: 'text-lg font-display font-bold text-blue-300' }, String(stats.upcomingCount || 0)),
      ]),
      h('div', { class: 'bg-base-900/40 rounded p-2' }, [
        h('div', { class: 'text-ink-400' }, 'Ocup.'),
        h('div', { class: 'text-lg font-display font-bold text-emerald-300' }, `${stats.occupancyPct || 0}%`),
      ]),
      h('div', { class: 'bg-base-900/40 rounded p-2' }, [
        h('div', { class: 'text-ink-400' }, 'Ingresos'),
        h('div', { class: 'text-sm font-display font-bold text-gold-300' }, formatEur(stats.estimatedRevenue || 0)),
      ]),
    ]) : null,
    h('div', { class: 'text-sm space-y-1' }, [
      p.currentValuation ? h('div', {}, ['Valor: ', h('span', { class: 'text-gold-300' }, formatEur(p.currentValuation))]) : null,
      p.monthlyMortgage ? h('div', {}, ['Hipoteca: ', h('span', { class: 'text-red-300' }, formatEur(p.monthlyMortgage) + '/mes')]) : null,
      p.bedrooms ? h('div', { class: 'text-ink-300' }, `${p.bedrooms} dorm · ${p.bathrooms || '?'} baños · ${p.maxGuests || '?'} pax`) : null,
      p.icalUrl ? h('div', { class: 'text-xs text-emerald-300' }, '✓ iCal Airbnb conectado') : h('div', { class: 'text-xs text-ink-400' }, 'iCal pendiente'),
    ]),
    h('div', { class: 'text-right text-xs text-ink-400 group-hover:text-gold-300 mt-2' }, 'Ver detalle →'),
  ]);
  return card;
}

// ============== MODAL DETALLE Inmueble (mini-dashboard facturación) =================
async function openPropertyDetail(p, container) {
  // Crear overlay
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl' });
  modal.appendChild(h('div', { class: 'p-5 border-b border-base-700 flex items-center justify-between sticky top-0 bg-base-900 z-10' }, [
    h('div', {}, [
      h('h3', { class: 'font-display font-bold text-xl' }, p.name),
      h('p', { class: 'text-xs text-ink-400 mt-1' }, p.address || p.city || ''),
    ]),
    h('button', { class: 'text-2xl text-ink-400 hover:text-ink-100', onclick: () => overlay.remove() }, '×'),
  ]));
  const body = h('div', { class: 'p-5 space-y-5' }, [
    h('div', { class: 'text-ink-300 text-sm py-6 text-center' }, 'Cargando datos del estudio...'),
  ]);
  modal.appendChild(body);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  try {
    const stats = await apiClient.get(`/api/v1/properties/${p.id}/stats`);
    body.innerHTML = '';
    body.appendChild(renderPropertyDashboard(stats, p, container, overlay));
  } catch (e) {
    body.innerHTML = '';
    body.appendChild(h('p', { class: 'text-red-400' }, 'Error: ' + (e.message || '')));
  }
}

function renderPropertyDashboard(stats, p, container, overlay) {
  const wrap = h('div', { class: 'space-y-6' });

  // KPIs principales
  wrap.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, [
    bigKpi('Reservas totales', stats.bookings.total || 0, '📅', 'blue'),
    bigKpi('Próximas', stats.bookings.upcoming || 0, '⏳', 'gold'),
    bigKpi('Ocupación ' + new Date().getFullYear(), `${stats.occupancy.occupancyPct}%`, '📊', 'green'),
    bigKpi('Próx. 30 días', `${stats.occupancy.occupancyNext30Pct}%`, '🔥', 'red'),
  ]));

  // Reserva actual o próxima
  if (stats.bookings.current) {
    const c = stats.bookings.current;
    wrap.appendChild(h('div', { class: 'bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-4' }, [
      h('div', { class: 'text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-1' }, '🟢 OCUPADO AHORA'),
      h('div', { class: 'font-display text-lg' }, c.guestName || 'Reservado'),
      h('div', { class: 'text-sm text-ink-300' }, `${formatDate(c.checkIn)} → ${formatDate(c.checkOut)}`),
    ]));
  } else if (stats.bookings.nextBooking) {
    const n = stats.bookings.nextBooking;
    wrap.appendChild(h('div', { class: 'bg-gold-500/10 border border-gold-400/30 rounded-xl p-4' }, [
      h('div', { class: 'text-xs uppercase tracking-wider text-gold-400 font-semibold mb-1' }, `⏳ PRÓXIMA RESERVA (en ${n.daysUntil} días)`),
      h('div', { class: 'font-display text-lg' }, n.guestName || 'Reservado'),
      h('div', { class: 'text-sm text-ink-300' }, `${formatDate(n.checkIn)} → ${formatDate(n.checkOut)}`),
    ]));
  }

  // Revenue panel
  wrap.appendChild(h('div', { class: 'bg-base-800/40 border border-base-700 rounded-xl p-4' }, [
    h('h4', { class: 'font-display font-semibold mb-3' }, '💰 Facturación estimada'),
    stats.revenue.nightlyRate > 0 ? h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, [
      ...[],
      revRow('Tarifa/noche', formatEur(stats.revenue.nightlyRate)),
      revRow('Ingresos año', formatEur(stats.revenue.estimatedThisYear), 'gold'),
      revRow('Próx. 30 días', formatEur(stats.revenue.estimatedNext30), 'green'),
      revRow('Gastos año', formatEur(stats.revenue.expensesThisYear), 'red'),
      revRow('Neto año', formatEur(stats.revenue.netThisYear), 'green'),
      revRow('Noches '+new Date().getFullYear(), stats.occupancy.nightsThisYear),
      revRow('Estancia media', `${stats.occupancy.avgStayNights} noches`),
      revRow('Días año', stats.occupancy.totalDaysInYear),
    ]) : h('div', { id: 'rate-suggestion', class: 'text-sm text-ink-400 italic' }, [
      h('span', {}, 'Cargando tarifa sugerida...'),
    ]),
  ]));

  // Timeline reservas
  if (stats.timeline.length > 0) {
    wrap.appendChild(h('div', { class: 'bg-base-800/40 border border-base-700 rounded-xl p-4' }, [
      h('h4', { class: 'font-display font-semibold mb-3' }, '📅 Reservas (desde iCal)'),
      h('div', { class: 'space-y-2 max-h-64 overflow-y-auto' },
        stats.timeline.map((b) => {
          const ci = new Date(b.checkIn).getTime();
          const isPast = b.checkOut && new Date(b.checkOut).getTime() < Date.now();
          const isCurrent = ci <= Date.now() && new Date(b.checkOut).getTime() > Date.now();
          return h('div', {
            class: `flex items-center justify-between text-sm bg-base-900/40 rounded-lg px-3 py-2 ${isPast ? 'opacity-50' : ''} ${isCurrent ? 'border border-emerald-400/40' : ''}`,
          }, [
            h('div', { class: 'flex items-center gap-2 min-w-0' }, [
              h('span', {}, isCurrent ? '🟢' : isPast ? '✓' : '⏳'),
              h('div', {}, [
                h('div', { class: 'font-medium' }, `${formatDate(b.checkIn)} → ${formatDate(b.checkOut)}`),
                h('div', { class: 'text-xs text-ink-400' }, [`${b.nights} noches`, b.source, b.guestName].filter(Boolean).join(' · ')),
              ]),
            ]),
            pill(b.source || 'manual', 'blue'),
          ]);
        })),
    ]));
  }

  // Cargar sugerencia de tarifa si no hay rate (async)
  if (!stats.revenue.nightlyRate) {
    setTimeout(async () => {
      try {
        const sug = await apiClient.get(`/api/v1/properties/${p.id}/suggest-rate`);
        const box = document.getElementById('rate-suggestion');
        if (!box) return;
        box.innerHTML = '';
        box.appendChild(h('div', { class: 'flex items-start gap-3' }, [
          h('span', { class: 'text-2xl' }, '💡'),
          h('div', { class: 'flex-1' }, [
            h('div', { class: 'text-sm text-ink-200' }, [
              h('strong', { class: 'text-gold-300 text-lg' }, `${sug.suggested}€/noche`),
              h('span', { class: 'text-xs text-ink-400 ml-2' }, `(${sug.confidence})`),
            ]),
            h('div', { class: 'text-xs text-ink-400 italic' }, sug.reason),
          ]),
          h('button', {
            class: 'text-xs bg-gold-400/20 hover:bg-gold-400/30 text-gold-300 px-3 py-1 rounded-lg border border-gold-400/30',
            onclick: async () => {
              await apiClient.patch(`/api/v1/properties/${p.id}`, { avgNightlyRate: sug.suggested });
              toast(`Tarifa fijada en ${sug.suggested}€/noche`, 'success');
              if (overlay) overlay.remove();
              renderInmuebles(container.parentElement || container);
            },
          }, 'Aplicar'),
        ]));
      } catch {}
    }, 200);
  }

  // Acciones
  wrap.appendChild(h('div', { class: 'flex gap-2 flex-wrap pt-3 border-t border-base-700' }, [
    btn('✏️ Editar inmueble', () => openEditProperty(p, container, overlay)),
    btn('🔄 Re-sincronizar iCal', async () => {
      try {
        await apiClient.post('/api/v1/properties/sync-ical', {});
        toast('Sync iCal completado', 'success');
        overlay.remove();
        renderInmuebles(container.parentElement || container);
      } catch (e) { toast('Error sync: ' + (e.message || ''), 'error'); }
    }, { variant: 'secondary' }),
    btn('📁 Ver archivos', () => { overlay.remove(); location.hash = '#archivos'; }, { variant: 'secondary' }),
    btn('💸 Añadir gasto', () => openExpenseModal(p, overlay), { variant: 'secondary' }),
    p.icalUrl ? btn('Abrir iCal Airbnb', () => window.open(p.icalUrl, '_blank'), { variant: 'secondary' }) : null,
    btn('🗑 Eliminar', async () => {
      const { openConfirm: oc } = await import('./_common.js');
      const ok = await oc({ title: 'Eliminar inmueble', message: p.name, danger: true, icon: '🗑️' });
      if (!ok) return;
      await apiClient.delete(`/api/v1/properties/${p.id}`);
      toast('Inmueble eliminado', 'success');
      overlay.remove();
      renderInmuebles(container.parentElement || container);
    }, { variant: 'secondary' }),
  ].filter(Boolean)));

  return wrap;
}

function revRow(label, value, color = '') {
  const c = { gold: 'text-gold-300', green: 'text-emerald-300', red: 'text-red-300' };
  return h('div', { class: 'bg-base-900/40 rounded-md px-3 py-2' }, [
    h('div', { class: 'text-[10px] uppercase tracking-wide text-ink-400' }, label),
    h('div', { class: `text-sm font-display font-bold ${c[color] || 'text-ink-100'}` }, String(value)),
  ]);
}

async function openEditProperty(p, container, parentOverlay) {
  const { openModal } = await import('./_common.js');
  openModal({
    title: 'Editar inmueble',
    icon: '🏠',
    size: 'lg',
    fields: [
      { name: 'name', label: 'Nombre', required: true, value: p.name },
      { name: 'address', label: 'Dirección', value: p.address || '' },
      { name: 'city', label: 'Ciudad', value: p.city || 'Alicante' },
      { name: 'operationMode', label: 'Modo operación', type: 'select', value: p.operationMode || 'short_term', options: [
        { value: 'short_term', label: 'Corto plazo (Airbnb/Booking)' },
        { value: 'long_term', label: 'Largo plazo' },
        { value: 'sale', label: 'Para venta' },
        { value: 'investment', label: 'Inversión' },
      ]},
      { name: 'icalUrl', label: 'iCal URL', value: p.icalUrl || '', placeholder: 'https://www.airbnb.es/calendar/ical/...' },
      { name: 'avgNightlyRate', label: '€ / noche promedio', type: 'number', value: p.avgNightlyRate || '' },
      { name: 'bedrooms', label: 'Dormitorios', type: 'number', value: p.bedrooms || '' },
      { name: 'bathrooms', label: 'Baños', type: 'number', value: p.bathrooms || '' },
      { name: 'maxGuests', label: 'Plazas máx.', type: 'number', value: p.maxGuests || '' },
      { name: 'sizeM2', label: 'Tamaño (m²)', type: 'number', value: p.sizeM2 || '' },
      { name: 'notes', label: 'Notas', type: 'textarea', rows: 3, value: p.notes || '' },
    ],
    onSubmit: async (vals) => {
      const payload = {
        ...vals,
        avgNightlyRate: vals.avgNightlyRate ? Number(vals.avgNightlyRate) : null,
        bedrooms: vals.bedrooms ? Number(vals.bedrooms) : null,
        bathrooms: vals.bathrooms ? Number(vals.bathrooms) : null,
        maxGuests: vals.maxGuests ? Number(vals.maxGuests) : null,
        sizeM2: vals.sizeM2 ? Number(vals.sizeM2) : null,
      };
      await apiClient.patch(`/api/v1/properties/${p.id}`, payload);
      toast('Inmueble actualizado', 'success');
      if (parentOverlay) parentOverlay.remove();
      renderInmuebles(container.parentElement || container);
    },
  });
}

async function openExpenseModal(p, parentOverlay) {
  const { openModal } = await import('./_common.js');
  openModal({
    title: 'Añadir gasto del inmueble',
    icon: '💸',
    fields: [
      { name: 'concept', label: 'Concepto', required: true, placeholder: 'p.ej. Reparación caldera' },
      { name: 'amount', label: 'Importe (€)', type: 'number', required: true },
      { name: 'category', label: 'Categoría', type: 'select', value: 'mantenimiento', options: ['mantenimiento','suministros','impuestos','seguros','limpieza','reformas','otro'] },
      { name: 'incurredAt', label: 'Fecha', type: 'date', value: new Date().toISOString().slice(0,10) },
      { name: 'notes', label: 'Notas', type: 'textarea', rows: 2 },
    ],
    onSubmit: async (vals) => {
      await apiClient.post('/api/v1/property-expenses', {
        propertyId: p.id,
        concept: vals.concept,
        amount: Number(vals.amount),
        category: vals.category,
        incurredAt: new Date(vals.incurredAt).getTime(),
        notes: vals.notes || null,
      });
      toast('Gasto añadido', 'success');
      if (parentOverlay) { parentOverlay.remove(); }
    },
  });
}

// ============================== TAB INGRESOS Airbnb ==============================
async function renderEarnings(container, root) {
  container.innerHTML = '';
  // Header + import button
  container.appendChild(h('div', { class: 'flex items-center justify-between mb-4 flex-wrap gap-3' }, [
    h('div', {}, [
      h('h3', { class: 'font-display font-bold text-xl' }, '💰 Ingresos Airbnb'),
      h('p', { class: 'text-sm text-ink-400' }, 'Datos reales tras importar CSV de Airbnb Transaction History'),
    ]),
    btn('📥 Importar CSV Airbnb', () => openAirbnbImportModal(root)),
  ]));

  const earnings = await apiClient.get('/api/v1/airbnb/earnings').catch(() => null);
  if (!earnings || earnings.bookingsWithRevenue === 0) {
    container.appendChild(h('div', { class: 'bg-gold-500/10 border border-gold-400/30 rounded-xl p-6 text-center' }, [
      h('div', { class: 'text-5xl mb-3' }, '📥'),
      h('h4', { class: 'font-display font-bold text-lg mb-2' }, 'No hay ingresos importados todavía'),
      h('p', { class: 'text-sm text-ink-300 mb-4 max-w-xl mx-auto' }, [
        'Para ver tus ingresos reales de Airbnb, descarga el CSV de Transaction History desde ',
        h('a', { href: 'https://www.airbnb.es/users/transaction_history', target: '_blank', class: 'text-gold-300 underline' }, 'airbnb.es/users/transaction_history'),
        ' y súbelo aquí. CARLOS OS lo parseará y vinculará con tus reservas.',
      ]),
      btn('📥 Importar CSV Airbnb ahora', () => openAirbnbImportModal(root)),
    ]));
    return;
  }

  // Stats top
  container.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-3 mb-5' }, [
    bigKpi('Ingresos brutos', formatEur(earnings.totalGross), '💰', 'gold'),
    bigKpi('Ingresos netos', formatEur(earnings.totalNet), '✅', 'green'),
    bigKpi('Reservas', earnings.bookingsWithRevenue, '📅', 'blue'),
    bigKpi('Año ' + new Date().getFullYear(), formatEur(earnings.thisYear.gross), '📈', 'purple'),
    bigKpi('Limpieza cobrada', formatEur(earnings.totalCleaning), '🧹', 'red'),
  ]));

  // Por estudio (real CSV + estimado tarifa × noches)
  container.appendChild(h('h4', { class: 'font-display font-semibold mb-3 mt-6' }, '🏠 Por estudio'));
  const propGrid = h('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-3 mb-6' });
  earnings.byProperty.forEach((p) => {
    const dataSource = p.dataSource || (p.bookings > 0 ? 'csv-real' : 'csv-real');
    const badge = dataSource === 'csv-real'
      ? h('span', { class: 'text-[10px] bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 px-1.5 py-0.5 rounded' }, '✓ real (CSV)')
      : dataSource === 'estimated'
        ? h('span', { class: 'text-[10px] bg-amber-500/15 border border-amber-400/30 text-amber-300 px-1.5 py-0.5 rounded' }, `≈ estimado (${formatEur(p.avgNightlyRate || 0)}/n)`)
        : h('span', { class: 'text-[10px] bg-sky-500/15 border border-sky-400/30 text-sky-300 px-1.5 py-0.5 rounded' }, `${p.bookingsReal || 0}/${p.bookings} real`);
    propGrid.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('div', { class: 'flex items-start justify-between mb-2 gap-2' }, [
        h('div', { class: 'font-semibold leading-tight flex-1 min-w-0' }, p.propertyName.replace(/^.*[—–-]\s*/, '')),
        badge,
      ]),
      h('div', { class: 'grid grid-cols-3 gap-2 text-xs' }, [
        h('div', {}, [h('div', { class: 'text-ink-400' }, 'Bruto'), h('div', { class: 'text-gold-300 text-lg font-bold' }, formatEur(p.gross))]),
        h('div', {}, [h('div', { class: 'text-ink-400' }, 'Neto'), h('div', { class: 'text-emerald-300 text-lg font-bold' }, formatEur(p.net))]),
        h('div', {}, [h('div', { class: 'text-ink-400' }, 'Reservas'), h('div', { class: 'text-blue-300 text-lg font-bold' }, String(p.bookings))]),
      ]),
    ]));
  });
  container.appendChild(propGrid);

  // Por mes — formato legible en español + orden cronológico ascendente
  container.appendChild(h('h4', { class: 'font-display font-semibold mb-3' }, '📅 Evolución mensual'));
  const MESES_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const formatMonth = (ym) => {
    // ym = "2026-05" → "Mayo 2026"
    const parts = String(ym || '').split('-');
    if (parts.length !== 2) return ym;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!y || !m || m < 1 || m > 12) return ym;
    return `${MESES_ES[m - 1]} ${y}`;
  };
  const monthsList = (earnings.byMonth || []).slice(-12); // últimos 12 meses, orden cronológico (más antiguo → más reciente)
  const maxMonth = Math.max(1, ...monthsList.map((m) => m.gross || 0));
  const totalRange = monthsList.reduce((s, m) => s + (m.gross || 0), 0);

  // Resumen rango
  if (monthsList.length > 0) {
    container.appendChild(h('div', { class: 'text-xs text-ink-400 mb-2' }, [
      `Mostrando ${monthsList.length} meses · Total facturado: `,
      h('span', { class: 'text-gold-300 font-semibold' }, formatEur(totalRange)),
    ]));
  }

  container.appendChild(h('div', { class: 'space-y-2' }, monthsList.map((m) => h('div', { class: 'bg-base-800/40 border border-base-700 rounded-lg p-3' }, [
    h('div', { class: 'flex items-center justify-between mb-1.5 flex-wrap gap-1' }, [
      h('span', { class: 'text-sm font-semibold text-ink-100' }, formatMonth(m.month)),
      h('div', { class: 'flex gap-3 text-xs items-baseline' }, [
        h('span', { class: 'text-gold-300 font-semibold text-sm' }, formatEur(m.gross || 0)),
        h('span', { class: 'text-emerald-300' }, 'neto ' + formatEur(m.net || 0)),
        h('span', { class: 'text-ink-400' }, `${m.bookings || 0} reservas`),
      ]),
    ]),
    h('div', { class: 'h-2 bg-base-700 rounded-full overflow-hidden' }, [
      h('div', { class: 'h-full bg-gradient-to-r from-gold-400 to-gold-500 transition-all', style: `width: ${Math.max(2, Math.round(((m.gross || 0) / maxMonth) * 100))}%` }),
    ]),
  ]))));
}

function openAirbnbImportModal(root) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  });
  let selectedFile = null;
  const status = h('div', { id: 'import-status' });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-2xl w-full p-6' }, [
    h('div', { class: 'flex items-center justify-between mb-4' }, [
      h('h3', { class: 'font-display font-bold text-lg' }, '📥 Importar CSV Airbnb'),
      h('button', { class: 'text-2xl text-ink-400', onclick: () => overlay.remove() }, '×'),
    ]),
    h('div', { class: 'bg-sky-500/10 border border-sky-400/30 rounded-lg p-3 mb-4 text-sm' }, [
      h('strong', { class: 'text-sky-300' }, '📌 Cómo obtener el CSV: '),
      'En Airbnb → menú → "Tu historial de transacciones" → ',
      h('a', { href: 'https://www.airbnb.es/users/transaction_history', target: '_blank', class: 'text-gold-300 underline' }, 'airbnb.es/users/transaction_history'),
      ' → botón "Exportar CSV" (arriba a la derecha). Selecciona "Reservas completadas" o "Todos los detalles".',
    ]),
    h('div', { class: 'mb-3' }, [
      h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase tracking-wide mb-1' }, 'Archivo CSV de Airbnb'),
      h('input', {
        type: 'file', accept: '.csv,text/csv',
        class: 'block w-full text-sm text-ink-200 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-base-700 file:text-ink-100 file:cursor-pointer hover:file:bg-base-600',
        onchange: (e) => { selectedFile = e.target.files?.[0]; },
      }),
    ]),
    status,
    h('div', { class: 'flex gap-2 justify-end mt-4' }, [
      h('button', { class: 'bg-base-700 hover:bg-base-600 text-ink-100 px-4 py-2 rounded-lg text-sm', onclick: () => overlay.remove() }, 'Cancelar'),
      h('button', {
        class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-5 py-2 rounded-lg text-sm',
        onclick: async () => {
          if (!selectedFile) { toast('Selecciona un CSV primero', 'warn'); return; }
          status.innerHTML = '<div class="text-sm text-ink-300 py-2">Procesando CSV...</div>';
          const fd = new FormData();
          fd.append('file', selectedFile);
          try {
            const res = await fetch('/api/v1/airbnb/import-csv', { method: 'POST', credentials: 'include', body: fd });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Error import');
            const s = data.stats;
            status.innerHTML = '';
            status.appendChild(h('div', { class: 'bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-4 text-sm space-y-1' }, [
              h('div', { class: 'font-semibold text-emerald-300 mb-2' }, '✅ Import completado'),
              h('div', {}, `📊 Filas procesadas: ${s.totalRows}`),
              h('div', {}, `🎯 Matched a tus estudios: ${s.matched}`),
              h('div', {}, `📝 Bookings actualizados: ${s.updated}`),
              h('div', {}, `➕ Bookings creados: ${s.createdMissing}`),
              h('div', { class: 'text-gold-300 font-bold mt-2' }, `💰 Total ingresos: ${formatEur(s.totalEarnings)}`),
              s.unmatchedListings && s.unmatchedListings.length > 0
                ? h('div', { class: 'text-xs text-amber-300 mt-2' }, `⚠️ Listings sin match: ${s.unmatchedListings.join(', ')}`)
                : null,
            ]));
            toast('CSV importado correctamente', 'success');
            setTimeout(() => { overlay.remove(); _activeTab = 'earnings'; renderInmuebles(root); }, 3000);
          } catch (e) {
            status.innerHTML = '';
            status.appendChild(h('div', { class: 'bg-red-500/10 border border-red-400/30 rounded-lg p-3 text-sm text-red-300' }, 'Error: ' + e.message));
          }
        },
      }, 'Importar'),
    ]),
  ]);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// ============================== TAB 2: Oportunidades ==============================
async function renderOpportunities(container) {
  container.innerHTML = '';

  // Stats top
  const stats = await apiClient.get('/api/v1/real-estate/stats').catch(() => ({ totals: {}, bySource: {} }));
  const totals = stats.totals || {};
  const statsBar = h('div', { class: 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5' }, [
    statTile('Total', totals.total || 0, '💎', 'gold'),
    statTile('Hot deals', totals.hotDeals || 0, '🔥', 'red'),
    statTile('Score medio', totals.avgScore || 0, '⭐', 'gold'),
    statTile('Nuevas', totals.new || 0, '🆕', 'blue'),
    statTile('En revisión', totals.reviewing || 0, '👀', 'gold'),
    statTile('Visitadas', totals.visited || 0, '🚶', 'green'),
  ]);
  container.appendChild(statsBar);

  // === WIZARD SCOUT PRO ===
  await renderScoutWizard(container);

  // Filtros
  const filters = h('div', { class: 'flex gap-2 mb-4 flex-wrap items-center' }, [
    h('input', {
      type: 'text', placeholder: '🔎 Buscar...', class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[200px]',
      oninput: (e) => { _oppFilters.q = e.target.value; renderOppList(); },
    }),
    selectFilter('Fuente', '', [
      { v: '', l: 'Todas' }, { v: 'idealista', l: 'Idealista' }, { v: 'fotocasa', l: 'Fotocasa' },
      { v: 'pisos.com', l: 'Pisos.com' }, { v: 'wallapop', l: 'Wallapop' }, { v: 'subastas', l: 'Subastas' },
    ], (v) => { _oppFilters.source = v; renderOppList(); }),
    selectFilter('Estado', '', [
      { v: '', l: 'Todos' }, { v: 'new', l: 'Nuevas' }, { v: 'reviewing', l: 'En revisión' },
      { v: 'contacted', l: 'Contactadas' }, { v: 'visited', l: 'Visitadas' }, { v: 'discarded', l: 'Descartadas' },
    ], (v) => { _oppFilters.status = v; renderOppList(); }),
    h('label', { class: 'text-xs text-ink-300 flex items-center gap-2' }, [
      'Score min:',
      h('input', {
        type: 'range', min: '0', max: '100', value: '0', class: 'w-24',
        oninput: (e) => { _oppFilters.minScore = Number(e.target.value); document.getElementById('score-val').textContent = e.target.value; renderOppList(); },
      }),
      h('span', { id: 'score-val', class: 'text-gold-300 font-mono w-6' }, '0'),
    ]),
  ]);
  container.appendChild(filters);

  const list = h('div', { id: 'opp-list' });
  container.appendChild(list);
  await renderOppList();
}

// ===== Scout PRO Wizard =====
async function renderScoutWizard(container) {
  const box = h('div', { class: 'bg-gradient-to-br from-gold-400/10 via-base-800/60 to-base-900/60 border border-gold-400/30 rounded-2xl p-5 mb-5' });
  container.appendChild(box);

  // Banner Idealista API (estado de conexión)
  await renderIdealistaConnectBanner(box);

  // Cargar criterios y zonas en paralelo
  const [critRes, zonesRes] = await Promise.all([
    apiClient.get('/api/v1/real-estate-scout/criteria').catch(() => ({ criteria: null })),
    apiClient.get('/api/v1/real-estate-scout/zones').catch(() => ({ zones: [] })),
  ]);
  const criteria = critRes.criteria || {
    zones: ['alicante-centro', 'elche', 'elda'],
    operation: 'sale',
    maxPrice: 250000,
    minM2: 40,
    minDiscountPct: 15,
    maxDiscountPct: 50,
    propertyTypes: ['piso', 'casa', 'atico', 'duplex', 'estudio', 'local_cambio_uso'],
    excludeOccupied: true,
    includeBuildableLocals: true,
  };
  const zones = zonesRes.zones || [];

  // Header
  box.appendChild(h('div', { class: 'flex items-center justify-between mb-4 flex-wrap gap-2' }, [
    h('div', {}, [
      h('h3', { class: 'font-display font-bold text-lg text-gold-300 flex items-center gap-2' }, ['🤖', 'Scout PRO · Alicante']),
      h('p', { class: 'text-xs text-ink-300 mt-0.5' }, 'Detecta oportunidades 15-30% por debajo del precio medio · Excluye ocupadas · Incluye locales con cambio de uso'),
    ]),
    h('div', { class: 'flex gap-2' }, [
      h('button', {
        id: 'btn-scout-run',
        class: 'btn btn-primary text-sm px-4 py-2',
        onclick: async () => await launchScout(),
      }, '🔄 Lanzar scout ahora'),
      h('button', {
        class: 'btn btn-ghost text-sm px-3 py-2',
        onclick: () => document.getElementById('scout-criteria-panel').classList.toggle('hidden'),
      }, '⚙️ Mis criterios'),
    ]),
  ]));

  // Panel criterios (colapsable)
  const panel = h('div', { id: 'scout-criteria-panel', class: 'hidden bg-base-900/60 border border-base-700 rounded-xl p-4 mt-2' });
  box.appendChild(panel);

  // Zonas (multi-select pills)
  panel.appendChild(h('div', { class: 'mb-3' }, [
    h('label', { class: 'text-xs text-ink-300 font-semibold mb-1.5 block' }, '📍 Zonas (haz click para activar/desactivar)'),
    h('div', { class: 'flex flex-wrap gap-1.5 max-h-40 overflow-y-auto' },
      zones.map((z) => zonePill(z, criteria.zones.includes(z.id), criteria))),
  ]));

  // Filtros numéricos
  panel.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-3' }, [
    numInput('💰 Precio máx €', criteria.maxPrice, (v) => criteria.maxPrice = v),
    numInput('📐 m² mínimos', criteria.minM2, (v) => criteria.minM2 = v),
    numInput('🎯 Descuento mín %', criteria.minDiscountPct, (v) => criteria.minDiscountPct = v),
    numInput('🚀 Descuento máx %', criteria.maxDiscountPct, (v) => criteria.maxDiscountPct = v),
  ]));

  // Toggles importantes
  panel.appendChild(h('div', { class: 'space-y-2 mb-3' }, [
    toggle('🚫 Excluir inmuebles ocupados/okupados', criteria.excludeOccupied, (v) => criteria.excludeOccupied = v, 'Filtra anuncios con "ocupada", "inquilino", "sin acceso", etc.'),
    toggle('🏗️ Incluir locales/edificios con cambio de uso aceptado', criteria.includeBuildableLocals, (v) => criteria.includeBuildableLocals = v, 'Solo locales con "cambio de uso", "apto vivienda", "edificable", etc.'),
  ]));

  // Tipos de inmueble
  const types = [
    { id: 'piso', l: 'Piso' }, { id: 'casa', l: 'Casa' }, { id: 'atico', l: 'Ático' },
    { id: 'duplex', l: 'Dúplex' }, { id: 'estudio', l: 'Estudio' }, { id: 'local_cambio_uso', l: 'Local (cambio uso)' },
  ];
  panel.appendChild(h('div', { class: 'mb-3' }, [
    h('label', { class: 'text-xs text-ink-300 font-semibold mb-1.5 block' }, '🏠 Tipo de inmueble'),
    h('div', { class: 'flex flex-wrap gap-1.5' }, types.map((t) => typePill(t, criteria.propertyTypes.includes(t.id), criteria))),
  ]));

  // Botón guardar
  panel.appendChild(h('div', { class: 'flex justify-end gap-2 mt-3' }, [
    h('button', {
      class: 'btn btn-ghost text-sm px-3 py-1.5',
      onclick: async () => {
        await apiClient.post('/api/v1/real-estate-scout/criteria', criteria);
        toast('✅ Criterios guardados');
      },
    }, '💾 Guardar criterios'),
  ]));

  // Estado de ejecución (resultado scout)
  const status = h('div', { id: 'scout-status', class: 'mt-3' });
  box.appendChild(status);
}

async function renderIdealistaConnectBanner(parent) {
  const status = await apiClient.get('/api/v1/integrations/idealista/status').catch(() => ({ connected: false }));
  const banner = h('div', { class: `rounded-xl p-3 mb-4 border ${status.connected ? 'bg-emerald-400/10 border-emerald-400/40' : 'bg-blue-400/10 border-blue-400/40'}` });
  parent.appendChild(banner);

  if (status.connected) {
    banner.appendChild(h('div', { class: 'flex items-center justify-between flex-wrap gap-2' }, [
      h('div', { class: 'text-sm text-emerald-200' }, '✅ Idealista API conectada · scout 100% operativo'),
      h('button', {
        class: 'text-xs px-2 py-1 rounded-lg bg-base-800 border border-base-700 hover:border-red-400/40 text-ink-300',
        onclick: async () => {
          await apiClient.delete('/api/v1/integrations/idealista/credentials');
          banner.remove();
          await renderIdealistaConnectBanner(parent);
        },
      }, '🔌 Desconectar'),
    ]));
    return;
  }

  banner.appendChild(h('div', { class: 'space-y-2' }, [
    h('div', { class: 'text-sm text-blue-200 font-semibold' }, '🔌 Conectar Idealista API (recomendado)'),
    h('div', { class: 'text-xs text-ink-300' }, 'Sin API el scout solo usa BOE Subastas (los portales bloquean scraping). Cuando recibas el email de developers.idealista.com pega aquí las claves:'),
    h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-2 mt-2' }, [
      h('input', { type: 'text', id: 'idealista-key', placeholder: 'API Key', class: 'bg-base-800 border border-base-700 rounded-lg px-2.5 py-1.5 text-sm font-mono' }),
      h('input', { type: 'password', id: 'idealista-secret', placeholder: 'API Secret', class: 'bg-base-800 border border-base-700 rounded-lg px-2.5 py-1.5 text-sm font-mono' }),
    ]),
    h('div', { class: 'flex gap-2 mt-1' }, [
      h('button', {
        class: 'btn btn-primary text-xs px-3 py-1.5',
        onclick: async () => {
          const key = document.getElementById('idealista-key').value.trim();
          const secret = document.getElementById('idealista-secret').value.trim();
          if (!key || !secret) { toast('⚠️ Pega API Key + Secret'); return; }
          await apiClient.post('/api/v1/integrations/idealista/credentials', { apiKey: key, apiSecret: secret });
          const test = await apiClient.post('/api/v1/integrations/idealista/test', {});
          if (test.ok) toast('✅ Conectado · ' + (test.sample?.length || 0) + ' resultados de test');
          else toast('⚠️ Credenciales guardadas pero el test no devolvió datos');
          banner.remove();
          await renderIdealistaConnectBanner(parent);
        },
      }, '💾 Guardar y probar'),
      h('a', {
        href: 'https://developers.idealista.com/access-request', target: '_blank', rel: 'noopener noreferrer',
        class: 'text-xs text-blue-300 hover:underline self-center',
      }, 'Solicitar acceso →'),
    ]),
  ]));
}

async function launchScout() {
  const btn = document.getElementById('btn-scout-run');
  const status = document.getElementById('scout-status');
  if (!btn || !status) return;
  btn.disabled = true;
  btn.textContent = '🔍 Buscando...';
  status.innerHTML = '<div class="text-xs text-ink-300 italic">Scrapeando Pisos.com · Habitaclia · BOE Subastas... (puede tardar 30-60s)</div>';
  try {
    const r = await apiClient.post('/api/v1/real-estate-scout/run', {});
    const found = r.found || 0;
    const inserted = r.inserted || 0;
    const filtered = r.filtered || 0;
    status.innerHTML = `<div class="text-sm bg-emerald-400/10 border border-emerald-400/30 rounded-lg px-3 py-2">✅ Scout completado · ${found} encontradas · ${inserted} oportunidades guardadas · ${filtered} descartadas por filtros</div>`;
    // Recargar lista
    await renderOppList();
  } catch (e) {
    status.innerHTML = `<div class="text-sm bg-red-400/10 border border-red-400/30 rounded-lg px-3 py-2">❌ Error: ${e.message || 'Scout falló'}</div>`;
  } finally {
    btn.disabled = false;
    btn.textContent = '🔄 Lanzar scout ahora';
  }
}

function zonePill(z, active, criteria) {
  const pill = h('button', {
    class: `text-xs px-2.5 py-1 rounded-full border transition-all ${active ? 'bg-gold-400/20 border-gold-400/60 text-gold-200' : 'bg-base-800 border-base-700 text-ink-300 hover:border-base-500'}`,
    onclick: () => {
      const isActive = criteria.zones.includes(z.id);
      if (isActive) {
        criteria.zones = criteria.zones.filter((x) => x !== z.id);
        pill.className = `text-xs px-2.5 py-1 rounded-full border transition-all bg-base-800 border-base-700 text-ink-300 hover:border-base-500`;
      } else {
        criteria.zones.push(z.id);
        pill.className = `text-xs px-2.5 py-1 rounded-full border transition-all bg-gold-400/20 border-gold-400/60 text-gold-200`;
      }
    },
  }, `${z.id.replace(/-/g, ' ')} · ${z.avg}€/m²`);
  return pill;
}

function typePill(t, active, criteria) {
  const pill = h('button', {
    class: `text-xs px-2.5 py-1 rounded-full border transition-all ${active ? 'bg-blue-400/20 border-blue-400/60 text-blue-200' : 'bg-base-800 border-base-700 text-ink-300 hover:border-base-500'}`,
    onclick: () => {
      const isActive = criteria.propertyTypes.includes(t.id);
      if (isActive) {
        criteria.propertyTypes = criteria.propertyTypes.filter((x) => x !== t.id);
        pill.className = `text-xs px-2.5 py-1 rounded-full border transition-all bg-base-800 border-base-700 text-ink-300 hover:border-base-500`;
      } else {
        criteria.propertyTypes.push(t.id);
        pill.className = `text-xs px-2.5 py-1 rounded-full border transition-all bg-blue-400/20 border-blue-400/60 text-blue-200`;
      }
    },
  }, t.l);
  return pill;
}

function numInput(label, value, onchange) {
  return h('label', { class: 'block' }, [
    h('div', { class: 'text-xs text-ink-300 mb-1' }, label),
    h('input', {
      type: 'number', value: String(value || 0),
      class: 'w-full bg-base-800 border border-base-700 rounded-lg px-2.5 py-1.5 text-sm font-mono',
      oninput: (e) => onchange(Number(e.target.value)),
    }),
  ]);
}

function toggle(label, checked, onchange, hint) {
  const cb = h('input', { type: 'checkbox', class: 'w-4 h-4 accent-gold-400', onchange: (e) => onchange(e.target.checked) });
  if (checked) cb.checked = true;
  return h('label', { class: 'flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-base-800/40' }, [
    cb,
    h('div', {}, [
      h('div', { class: 'text-sm text-ink-100' }, label),
      hint ? h('div', { class: 'text-[10px] text-ink-400 mt-0.5' }, hint) : null,
    ]),
  ]);
}

async function renderOppList() {
  const list = document.getElementById('opp-list');
  if (!list) return;
  list.innerHTML = '<div class="text-ink-300 text-sm py-6 text-center">Cargando oportunidades...</div>';
  const res = await apiClient.get('/api/v1/real-estate/opportunities').catch(() => ({ data: [] }));
  let opps = res.data || [];
  if (_oppFilters.source) opps = opps.filter((o) => o.source === _oppFilters.source);
  if (_oppFilters.status) opps = opps.filter((o) => o.status === _oppFilters.status);
  if (_oppFilters.minScore) opps = opps.filter((o) => (o.opportunityScore || 0) >= _oppFilters.minScore);
  if (_oppFilters.q) {
    const q = _oppFilters.q.toLowerCase();
    opps = opps.filter((o) => [o.title, o.address, o.city, o.zone].some((x) => (x || '').toLowerCase().includes(q)));
  }
  list.innerHTML = '';
  if (opps.length === 0) {
    list.appendChild(emptyState('🔍', 'Sin oportunidades todavía', 'Pulsa "🔄 Lanzar scout ahora" arriba. Si Idealista API no está conectada, solo verás resultados de BOE Subastas.'));
    return;
  }
  // Separar las que cumplen el threshold (isMatch=true) de las referencias
  const matches = opps.filter((o) => o.isMatch !== false);
  const refs = opps.filter((o) => o.isMatch === false);

  if (matches.length > 0) {
    list.appendChild(h('div', { class: 'flex items-center gap-2 mb-2 mt-1' }, [
      h('span', { class: 'text-xs font-bold text-emerald-300 uppercase tracking-wide' }, `✅ Cumplen tu criterio (${matches.length})`),
      h('span', { class: 'text-[10px] text-ink-400' }, '— descuento ≥ tu mínimo'),
    ]));
    const grid1 = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-5' });
    matches.forEach((o) => grid1.appendChild(oppCard(o)));
    list.appendChild(grid1);
  }
  if (refs.length > 0) {
    list.appendChild(h('div', { class: 'flex items-center gap-2 mb-2 mt-1 pt-3 border-t border-base-700/50' }, [
      h('span', { class: 'text-xs font-bold text-ink-300 uppercase tracking-wide' }, `📊 Referencias (${refs.length})`),
      h('span', { class: 'text-[10px] text-ink-400' }, '— por debajo de tu mínimo, sirven para comparar mercado'),
    ]));
    const grid2 = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 opacity-75' });
    refs.forEach((o) => grid2.appendChild(oppCard(o)));
    list.appendChild(grid2);
  }
}

function tierLabel(tier) {
  return {
    chollo: { l: '🔥 CHOLLO ≥30%', c: 'red' },
    muy_buena: { l: '⭐ MUY BUENA ≥20%', c: 'red' },
    buena: { l: '✅ BUENA ≥15%', c: 'green' },
    aceptable: { l: '👌 ACEPTABLE ≥10%', c: 'gold' },
    cerca_mercado: { l: '~ CERCA MERCADO ≥5%', c: 'blue' },
    referencia: { l: 'REFERENCIA', c: 'gray' },
  }[tier] || { l: tier, c: 'gray' };
}

function oppCard(o) {
  const score = o.opportunityScore || 0;
  const scoreColor = score >= 85 ? 'red' : score >= 70 ? 'gold' : score >= 50 ? 'blue' : 'gray';
  const statusColor = { new: 'blue', reviewing: 'gold', contacted: 'gold', visited: 'green', negotiating: 'gold', bought: 'green', discarded: 'gray' }[o.status] || 'gray';
  return h('div', {
    class: `relative bg-base-800/50 border ${score >= 85 ? 'border-red-400/40' : 'border-base-700'} rounded-xl p-5 hover:border-gold-400/40 transition-all cursor-pointer`,
    onclick: () => openOppDetail(o),
  }, [
    // Score badge (top-right)
    h('div', { class: 'absolute top-3 right-3 flex items-center gap-2' }, [
      o.starred ? h('span', { class: 'text-gold-300 text-lg' }, '★') : null,
      h('div', {
        class: `w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold text-lg ${score >= 85 ? 'border-red-400 text-red-300 bg-red-400/10' : score >= 70 ? 'border-gold-400 text-gold-300 bg-gold-400/10' : 'border-base-600 text-ink-300'}`,
      }, String(score)),
    ]),
    h('div', { class: 'mb-2 pr-16' }, [
      h('h4', { class: 'font-display font-semibold leading-tight' }, o.title || `${o.propertyType || 'Inmueble'} en ${o.city || o.zone || '—'}`),
      h('p', { class: 'text-xs text-ink-400 mt-0.5' }, [o.address, o.zone, o.city].filter(Boolean).join(' · ') || '—'),
    ]),
    h('div', { class: 'flex flex-wrap gap-1.5 mb-3' }, [
      o.tier ? pill(tierLabel(o.tier).l, tierLabel(o.tier).c) : null,
      pill(o.source || '—', 'blue'),
      pill(o.status || 'new', statusColor),
      o.operation ? pill(o.operation === 'sale' ? 'Venta' : 'Alquiler', 'gray') : null,
      o.propertyType ? pill(o.propertyType, 'gray') : null,
    ].filter(Boolean)),
    h('div', { class: 'grid grid-cols-2 gap-2 text-xs mb-3' }, [
      o.listingPrice ? metric('Precio', formatEur(o.listingPrice), 'gold') : null,
      o.pricePerM2 ? metric('€/m²', formatEur(o.pricePerM2)) : null,
      o.sizeM2 ? metric('Superficie', `${o.sizeM2} m²`) : null,
      o.grossYield ? metric('Yield bruto', `${o.grossYield.toFixed(1)}%`, 'green') : null,
      o.pctBelowAvg ? metric('Bajo media', `${o.pctBelowAvg.toFixed(1)}%`, o.pctBelowAvg > 0 ? 'green' : 'red') : null,
      o.roiYears ? metric('ROI', `${o.roiYears.toFixed(1)} años`) : null,
    ].filter(Boolean)),
    o.rationale ? h('p', { class: 'text-xs text-ink-300 line-clamp-2 italic' }, `"${o.rationale.slice(0, 140)}"`) : null,
    h('div', { class: 'flex items-center justify-between mt-3 pt-3 border-t border-base-700/50' }, [
      h('span', { class: 'text-[10px] text-ink-400' }, o.scoutAgent ? `🤖 ${o.scoutAgent}` : ''),
      o.externalUrl ? h('a', {
        href: o.externalUrl, target: '_blank', rel: 'noopener noreferrer',
        class: 'text-xs text-gold-300 hover:underline',
        onclick: (e) => e.stopPropagation(),
      }, 'Ver anuncio →') : null,
    ]),
  ]);
}

function metric(label, value, color = '') {
  const colors = { gold: 'text-gold-300', green: 'text-emerald-300', red: 'text-red-300' };
  return h('div', { class: 'flex justify-between bg-base-900/40 rounded-md px-2 py-1' }, [
    h('span', { class: 'text-ink-400' }, label),
    h('span', { class: `font-semibold ${colors[color] || 'text-ink-100'}` }, value),
  ]);
}

function statTile(label, value, emoji, color) {
  const colors = {
    gold: 'border-gold-400/30 text-gold-300', green: 'border-emerald-400/30 text-emerald-300',
    red: 'border-red-400/30 text-red-300', blue: 'border-sky-400/30 text-sky-300',
  };
  return h('div', { class: `bg-base-800/50 border ${colors[color] || 'border-base-700'} rounded-xl p-3` }, [
    h('div', { class: 'text-xs text-ink-300 flex items-center gap-1' }, [emoji, label]),
    h('div', { class: 'text-2xl font-display font-bold' }, String(value)),
  ]);
}

function selectFilter(label, value, options, onchange) {
  const sel = h('select', {
    class: 'bg-base-800 border border-base-700 rounded-lg px-2 py-1.5 text-sm',
    onchange: (e) => onchange(e.target.value),
  }, options.map((o) => {
    const opt = document.createElement('option');
    opt.value = o.v;
    opt.textContent = o.l;
    if (o.v === value) opt.selected = true;
    return opt;
  }));
  return sel;
}

// ============================== TAB 3: Predicciones ==============================
async function renderPredictions(container) {
  container.innerHTML = '';
  const res = await apiClient.get('/api/v1/real-estate/predictions').catch(() => ({ data: [] }));
  const preds = res.data || [];
  if (preds.length === 0) {
    container.appendChild(emptyState('🔮', 'Sin predicciones todavía', 'Los agentes predictores generan proyecciones a 2, 4, 6 y 12 meses con datos oficiales (INE, BdE, Idealista index). Lanza los scouts para empezar.'));
    return;
  }
  // Agrupar por horizonte
  const byH = { 2: [], 4: [], 6: [], 12: [] };
  preds.forEach((p) => { if (byH[p.horizonMonths]) byH[p.horizonMonths].push(p); });
  [2, 4, 6, 12].forEach((m) => {
    if (!byH[m].length) return;
    container.appendChild(h('h3', { class: 'font-display font-semibold text-lg mt-5 mb-2 text-gold-300' }, `Horizonte ${m} meses`));
    const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
    byH[m].forEach((p) => grid.appendChild(predCard(p)));
    container.appendChild(grid);
  });
}

function predCard(p) {
  const dir = p.direction;
  const dirEmoji = dir === 'up' ? '📈' : dir === 'down' ? '📉' : '➡️';
  const dirColor = dir === 'up' ? 'text-emerald-300' : dir === 'down' ? 'text-red-300' : 'text-ink-300';
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('div', { class: 'flex items-start justify-between mb-2' }, [
      h('h4', { class: 'font-display font-semibold' }, p.geography),
      h('div', { class: `text-2xl ${dirColor}` }, dirEmoji),
    ]),
    h('div', { class: 'text-xs text-ink-400 mb-2' }, [p.propertyType, p.operation === 'sale' ? 'Venta' : p.operation === 'rent' ? 'Alquiler' : ''].filter(Boolean).join(' · ')),
    h('div', { class: 'flex items-baseline gap-3 mb-2' }, [
      h('span', { class: `text-3xl font-display font-bold ${dirColor}` }, p.expectedPctChange != null ? `${p.expectedPctChange > 0 ? '+' : ''}${p.expectedPctChange.toFixed(1)}%` : '—'),
      p.confidence != null ? pill(`${p.confidence}% conf.`, 'gold') : null,
    ]),
    p.rationale ? h('p', { class: 'text-xs text-ink-300 line-clamp-3' }, p.rationale) : null,
  ]);
}

// ============================== Detalle de oportunidad (modal) ==============================
function openOppDetail(o) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  }, [
    h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto' }, [
      // Header
      h('div', { class: 'p-5 border-b border-base-700 flex items-start justify-between sticky top-0 bg-base-900' }, [
        h('div', {}, [
          h('h3', { class: 'font-display font-bold text-xl' }, o.title || 'Oportunidad'),
          h('p', { class: 'text-xs text-ink-400 mt-1' }, [o.address, o.zone, o.city].filter(Boolean).join(' · ')),
        ]),
        h('button', { class: 'text-ink-400 hover:text-ink-100 text-2xl leading-none', onclick: () => overlay.remove() }, '×'),
      ]),
      // Body
      h('div', { class: 'p-5 space-y-4' }, [
        // Score grande
        h('div', { class: 'flex items-center gap-4' }, [
          h('div', {
            class: `w-20 h-20 rounded-full border-4 flex items-center justify-center font-display font-bold text-3xl ${(o.opportunityScore || 0) >= 85 ? 'border-red-400 text-red-300' : (o.opportunityScore || 0) >= 70 ? 'border-gold-400 text-gold-300' : 'border-base-600 text-ink-300'}`,
          }, String(o.opportunityScore || 0)),
          h('div', { class: 'flex-1' }, [
            h('div', { class: 'text-xs text-ink-300' }, 'Opportunity Score'),
            h('div', { class: 'text-sm' }, o.confidence != null ? `Confianza: ${o.confidence}%` : ''),
            o.starred ? h('span', { class: 'text-gold-300 text-lg' }, '★ Marcada') : null,
          ]),
        ]),
        // Métricas
        h('div', { class: 'grid grid-cols-2 md:grid-cols-3 gap-2 text-sm' }, [
          o.listingPrice ? bigMetric('Precio anuncio', formatEur(o.listingPrice), 'gold') : null,
          o.pricePerM2 ? bigMetric('€/m²', formatEur(o.pricePerM2)) : null,
          o.zoneAvgPpm2 ? bigMetric('€/m² zona', formatEur(o.zoneAvgPpm2)) : null,
          o.sizeM2 ? bigMetric('Superficie', `${o.sizeM2} m²`) : null,
          o.rooms ? bigMetric('Habitaciones', String(o.rooms)) : null,
          o.bathrooms ? bigMetric('Baños', String(o.bathrooms)) : null,
          o.yearBuilt ? bigMetric('Año', String(o.yearBuilt)) : null,
          o.energyRating ? bigMetric('Eficiencia', o.energyRating) : null,
          o.estimatedRent ? bigMetric('Renta estimada', `${formatEur(o.estimatedRent)}/mes`, 'green') : null,
          o.grossYield ? bigMetric('Yield bruto', `${o.grossYield.toFixed(2)}%`, 'green') : null,
          o.netYield ? bigMetric('Yield neto', `${o.netYield.toFixed(2)}%`, 'green') : null,
          o.roiYears ? bigMetric('ROI años', o.roiYears.toFixed(1)) : null,
          o.pctBelowAvg ? bigMetric('% bajo media', `${o.pctBelowAvg.toFixed(1)}%`, o.pctBelowAvg > 0 ? 'green' : 'red') : null,
        ].filter(Boolean)),
        // Rationale
        o.rationale ? h('div', {}, [
          h('h4', { class: 'font-semibold mb-1 text-sm' }, '🧠 Razonamiento del agente'),
          h('p', { class: 'text-sm text-ink-200 bg-base-800/50 p-3 rounded-lg' }, o.rationale),
        ]) : null,
        // Risks
        o.risks && o.risks.length ? h('div', {}, [
          h('h4', { class: 'font-semibold mb-1 text-sm text-red-300' }, '⚠️ Riesgos'),
          h('ul', { class: 'text-sm text-ink-200 space-y-1' }, o.risks.map((r) => h('li', { class: 'flex gap-2' }, ['•', r]))),
        ]) : null,
        // Next actions
        o.nextActions && o.nextActions.length ? h('div', {}, [
          h('h4', { class: 'font-semibold mb-1 text-sm text-emerald-300' }, '🎯 Próximos pasos'),
          h('ul', { class: 'text-sm text-ink-200 space-y-1' }, o.nextActions.map((r) => h('li', { class: 'flex gap-2' }, ['•', r]))),
        ]) : null,
        // Notas usuario
        h('div', {}, [
          h('h4', { class: 'font-semibold mb-1 text-sm' }, '📝 Mis notas'),
          h('textarea', {
            id: 'opp-notes', class: 'w-full bg-base-800 border border-base-700 rounded-lg p-2 text-sm', rows: '3',
            placeholder: 'Tus comentarios sobre esta oportunidad...',
          }, o.userNotes || ''),
        ]),
        // Status changer
        h('div', { class: 'flex gap-2 flex-wrap' }, [
          ...['new', 'reviewing', 'contacted', 'visited', 'negotiating', 'bought', 'discarded'].map((s) => h('button', {
            class: `text-xs px-3 py-1 rounded-full border transition ${o.status === s ? 'bg-gold-400/20 border-gold-400 text-gold-300' : 'border-base-600 text-ink-300 hover:border-gold-400/50'}`,
            onclick: async () => {
              await apiClient.patch(`/api/v1/real-estate/opportunities/${o.id}`, { status: s, userNotes: document.getElementById('opp-notes')?.value });
              o.status = s;
              overlay.remove();
              const root = document.getElementById('view-root');
              if (root) renderActiveTab(root);
            },
          }, statusLabel(s))),
        ]),
      ]),
      // Footer
      h('div', { class: 'p-4 border-t border-base-700 flex gap-2 justify-end sticky bottom-0 bg-base-900' }, [
        btn(o.starred ? '★ Quitar destacado' : '☆ Destacar', async () => {
          await apiClient.patch(`/api/v1/real-estate/opportunities/${o.id}`, { starred: !o.starred });
          o.starred = !o.starred;
          overlay.remove();
          const root = document.getElementById('view-root');
          if (root) renderActiveTab(root);
        }, { variant: 'secondary' }),
        o.externalUrl ? h('a', {
          href: o.externalUrl, target: '_blank', rel: 'noopener noreferrer',
          class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-4 py-2 rounded-lg text-sm',
        }, 'Ver anuncio original →') : null,
      ]),
    ]),
  ]);
  document.body.appendChild(overlay);
}

function statusLabel(s) {
  return { new: 'Nueva', reviewing: 'Revisando', contacted: 'Contactada', visited: 'Visitada', negotiating: 'Negociando', bought: 'Comprada', discarded: 'Descartada' }[s] || s;
}

function bigMetric(label, value, color = '') {
  const colors = { gold: 'text-gold-300', green: 'text-emerald-300', red: 'text-red-300' };
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-2' }, [
    h('div', { class: 'text-[10px] uppercase tracking-wide text-ink-400' }, label),
    h('div', { class: `font-display font-bold ${colors[color] || 'text-ink-100'}` }, value),
  ]);
}

// ============================== Modal añadir inmueble propio ==============================
function openPropertyModal(root, onDone) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  }, [
    h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-md w-full' }, [
      h('div', { class: 'p-5 border-b border-base-700 flex items-center justify-between' }, [
        h('h3', { class: 'font-display font-bold text-lg' }, '🏠 Añadir inmueble'),
        h('button', { class: 'text-ink-400 text-2xl leading-none', onclick: () => overlay.remove() }, '×'),
      ]),
      h('form', {
        class: 'p-5 space-y-3',
        onsubmit: async (e) => {
          e.preventDefault();
          const name = overlay.querySelector('input[name=name]').value.trim();
          const city = overlay.querySelector('input[name=city]').value.trim();
          const mode = overlay.querySelector('select[name=mode]').value;
          if (!name) return;
          const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          try {
            await apiClient.post('/api/v1/properties', { name, slug, city, operationMode: mode });
            overlay.remove();
            if (typeof onDone === 'function') onDone();
          } catch (err) {
            toast(err?.message || 'No se pudo crear', 'error');
          }
        },
      }, [
        field('Nombre', h('input', { name: 'name', class: 'w-full bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm', required: true, placeholder: 'p.ej. Piso Alicante centro' })),
        field('Ciudad', h('input', { name: 'city', class: 'w-full bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm', placeholder: 'Alicante' })),
        field('Modo de operación', h('select', { name: 'mode', class: 'w-full bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm' }, [
          h('option', { value: 'short_term' }, 'Corto plazo (Airbnb/Booking)'),
          h('option', { value: 'long_term' }, 'Largo plazo'),
          h('option', { value: 'sale' }, 'Para venta'),
          h('option', { value: 'investment' }, 'Inversión / espera'),
        ])),
        h('div', { class: 'flex gap-2 justify-end pt-2' }, [
          h('button', { type: 'button', class: 'bg-base-700 hover:bg-base-600 text-ink-100 px-4 py-2 rounded-lg text-sm', onclick: () => overlay.remove() }, 'Cancelar'),
          h('button', { type: 'submit', class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-4 py-2 rounded-lg text-sm' }, 'Crear'),
        ]),
      ]),
    ]),
  ]);
  document.body.appendChild(overlay);
}

function field(label, input) {
  return h('div', {}, [
    h('label', { class: 'block text-xs font-semibold text-ink-300 mb-1' }, label),
    input,
  ]);
}

// ============================== Lanzar Scouts manualmente ==============================
async function runScouts(root) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4',
  }, [
    h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-md w-full p-6 text-center' }, [
      h('div', { class: 'text-5xl mb-3' }, '🤖'),
      h('h3', { class: 'font-display font-bold text-lg mb-2' }, 'Lanzando scouts inmobiliarios'),
      h('p', { class: 'text-sm text-ink-300 mb-4' }, 'Los agentes están explorando Idealista, Fotocasa, Pisos.com, Wallapop y subastas judiciales en busca de chollos.'),
      h('div', { class: 'flex justify-center' }, [
        h('div', { class: 'w-10 h-10 border-4 border-gold-400/30 border-t-gold-400 rounded-full animate-spin' }),
      ]),
    ]),
  ]);
  document.body.appendChild(overlay);
  try {
    await apiClient.post('/api/v1/ai/dispatch', {
      mode: 'panel',
      panel: ['scout-idealista', 'scout-fotocasa', 'scout-pisos-com', 'scout-wallapop-inmuebles', 'scout-subastas-judiciales', 'predictor-mercado-2m', 'predictor-mercado-6m', 'analista-oportunidades-final'],
      input: 'Busca chollos inmobiliarios en Alicante y Costa Blanca con ROI > 6% bruto. Estrictamente con datos oficiales. Genera tarjetas en mi dashboard.',
    });
  } catch (err) {
    // silent
  }
  setTimeout(async () => {
    overlay.remove();
    _activeTab = 'opportunities';
    await renderActiveTab(root);
  }, 1500);
}
// ============================================================================
// 📅 CALENDARIOS AIRBNB v3 · selector + estados + saturación real al filler
// ============================================================================
//
// LÓGICA:
// - 3 anuncios "estudio" (E1, E2, E3) = espacios físicos reales (min 10 noches)
// - 1 anuncio "filler" (Anuncio 4 / Min 3 noches) = comodín · físicamente
//   ocupa uno de los 3 estudios cuando recibe reserva
// - Día "saturado" = los 3 estudios reales reservados a la vez
// - SATURACIÓN se pinta en ROJO SOLO en el calendario del filler
//   (significa: no hay espacio físico para aceptar reservas cortas)

let _calMonthOffset = 0;
let _calSlots = [null, null, null, null];
let _calAllProps = [];
let _calBookingsCache = {};

function _isFillerProp(p) {
  if (!p) return false;
  const n = (p.name || '').toLowerCase();
  return n.includes('rellenar') || n.includes('min 3') || n.includes('mínimo 3') || n.includes('anuncio 4') || n.includes('filler') || n.includes('overflow');
}

let _calAutoRefreshTimer = null;

async function renderCalendars(container) {
  container.innerHTML = '';
  // Auto-refresh cada 60s: invalida cache y re-renderiza para que cualquier cambio en Airbnb
  // se vea como máximo 60s después del último sync iCal (cron cada 2min)
  if (_calAutoRefreshTimer) clearInterval(_calAutoRefreshTimer);
  _calAutoRefreshTimer = setInterval(() => {
    if (document.body.contains(container)) {
      _calBookingsCache = {};
      renderCalendars(container);
    } else {
      clearInterval(_calAutoRefreshTimer);
      _calAutoRefreshTimer = null;
    }
  }, 60000);
  try {
    // Forzar sync iCal al entrar (sin esperar respuesta para no bloquear UI)
    apiClient.post('/api/v1/properties/sync-ical', {}).catch(() => {});
    if (!_calAllProps.length) {
      const propsRes = await apiClient.get('/api/v1/properties');
      _calAllProps = (propsRes?.data || propsRes || []).filter(p => p.icalUrl || p.ical_url);
    }
    if (!_calAllProps.length) {
      container.appendChild(emptyState('📅', 'Sin calendarios', 'No hay propiedades con URL iCal de Airbnb conectada.'));
      return;
    }

    // Identificar los 3 estudios reales y el filler
    const studios = _calAllProps.filter(p => !_isFillerProp(p));
    const filler = _calAllProps.find(p => _isFillerProp(p));

    // Inicializar slots: por defecto E1, E2, E3, Filler
    if (!_calSlots[0] || !_calAllProps.find(p => p.id === _calSlots[0])) _calSlots[0] = studios[0]?.id || _calAllProps[0]?.id;
    if (!_calSlots[1] || !_calAllProps.find(p => p.id === _calSlots[1])) _calSlots[1] = studios[1]?.id || _calAllProps[1]?.id;
    if (!_calSlots[2] || !_calAllProps.find(p => p.id === _calSlots[2])) _calSlots[2] = studios[2]?.id || _calAllProps[2]?.id;
    if (!_calSlots[3] || !_calAllProps.find(p => p.id === _calSlots[3])) _calSlots[3] = filler?.id || _calAllProps[3]?.id;

    const monthDate = new Date();
    monthDate.setMonth(monthDate.getMonth() + _calMonthOffset);
    const monthLabel = monthDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

    // Header
    const head = h('div', { class: 'flex items-center justify-between mb-4 flex-wrap gap-2' }, [
      h('div', { class: 'flex items-center gap-2' }, [
        h('button', { class: 'px-3 py-1.5 rounded-lg bg-base-800 hover:bg-base-700 text-ink-100', onclick: () => { _calMonthOffset--; renderCalendars(container); } }, '◀'),
        h('div', { class: 'px-4 py-1.5 font-semibold text-lg capitalize min-w-[180px] text-center' }, monthLabel),
        h('button', { class: 'px-3 py-1.5 rounded-lg bg-base-800 hover:bg-base-700 text-ink-100', onclick: () => { _calMonthOffset++; renderCalendars(container); } }, '▶'),
        h('button', { class: 'px-3 py-1.5 rounded-lg bg-base-800 hover:bg-base-700 text-ink-300 text-xs', onclick: () => { _calMonthOffset = 0; renderCalendars(container); } }, 'Hoy'),
      ]),
      h('div', { class: 'flex items-center gap-3 text-xs flex-wrap' }, [
        h('div', { class: 'flex items-center gap-1.5' }, [ h('div', { class: 'w-3 h-3 rounded bg-emerald-500/70' }), h('span', { class: 'text-ink-300' }, 'Reservado') ]),
        h('div', { class: 'flex items-center gap-1.5' }, [ h('div', { class: 'w-3 h-3 rounded', style: 'background-image: repeating-linear-gradient(45deg, #94a3b8 0px, #94a3b8 3px, #475569 3px, #475569 7px); background-color: #64748b;' }), h('span', { class: 'text-ink-300' }, 'Bloqueado manual') ]),
        h('div', { class: 'flex items-center gap-1.5' }, [ h('div', { class: 'w-3 h-3 rounded', style: 'background-image: repeating-linear-gradient(45deg, #f59e0b 0px, #f59e0b 3px, #92400e 3px, #92400e 7px); background-color: #b45309;' }), h('span', { class: 'text-ink-300' }, 'Auto (gap < 10 noches)') ]),
        h('div', { class: 'flex items-center gap-1.5' }, [ h('div', { class: 'w-3 h-3 rounded bg-rose-500/80' }), h('span', { class: 'text-ink-300' }, 'Sin sitio (3 estudios llenos)') ]),
        h('div', { class: 'flex items-center gap-1.5' }, [ h('div', { class: 'w-3 h-3 rounded bg-base-700' }), h('span', { class: 'text-ink-300' }, 'Libre') ]),
        h('div', { class: 'flex items-center gap-1.5' }, [ h('div', { class: 'w-3 h-3 rounded ring-1 ring-gold-400' }), h('span', { class: 'text-ink-300' }, 'Hoy') ]),
        h('span', { class: 'text-xs text-ink-300' }, 'Auto-refresh 60s · Sync 2min'),
        h('button', { class: 'ml-2 px-3 py-1.5 rounded-lg bg-gold-500/20 text-gold-300 hover:bg-gold-500/30 text-xs font-semibold', onclick: async () => {
          try {
            await apiClient.post('/api/v1/properties/sync-ical', {});
            toast('✓ Sincronizando iCals...');
            _calBookingsCache = {};
            setTimeout(() => renderCalendars(container), 1500);
          } catch (e) { toast('⚠️ ' + (e.message || 'Error')); }
        }}, '🔄 Sync ahora'),
      ]),
    ]);
    container.appendChild(head);

    // Cargar bookings de TODAS las propiedades (necesitamos los 3 estudios reales para saturación)
    const allPropIds = _calAllProps.map(p => p.id);
    await Promise.all(allPropIds.map(async (pid) => {
      if (_calBookingsCache[pid]) return;
      try {
        const r = await apiClient.get('/api/v1/properties/' + pid + '/bookings');
        _calBookingsCache[pid] = r?.data || r || [];
      } catch (e) { _calBookingsCache[pid] = []; }
    }));

    // Calcular días saturados: los 3 estudios reales TODOS reservados
    const studiosBookings = studios.map(p => _calBookingsCache[p.id] || []);
    const saturatedDays = computeSaturatedDays(studiosBookings, monthDate);

    const grid = h('div', { class: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3' });
    container.appendChild(grid);

    for (let i = 0; i < 4; i++) {
      const card = h('div', { class: 'rounded-xl border border-base-700 bg-base-800/40 p-2.5' });
      const slotPid = _calSlots[i];
      const slotProp = _calAllProps.find(p => p.id === slotPid);
      const slotBookings = slotPid ? (_calBookingsCache[slotPid] || []) : [];
      const isFillerSlot = _isFillerProp(slotProp);
      renderMiniCalendar(card, slotProp, slotBookings, monthDate, saturatedDays, isFillerSlot, i, container);
      grid.appendChild(card);
    }

    // Nota explicativa
    container.appendChild(h('div', { class: 'mt-4 text-xs text-ink-300 bg-base-800/40 rounded-xl px-4 py-3 border border-base-700' }, [
      h('div', { class: 'font-semibold text-ink-100 mb-1' }, '💡 Cómo leer los calendarios'),
      h('div', {}, 'Cada anuncio del filler (Min 3 noches) ocupa físicamente uno de los 3 estudios. Si los 3 estudios están reservados un mismo día, NO PUEDES aceptar una reserva nueva en el anuncio filler — esos días aparecen en ROJO solo en su calendario.'),
    ]));
  } catch (err) {
    container.appendChild(emptyState('⚠️', 'Error', err?.message || 'No se pudo cargar.'));
  }
}

function computeSaturatedDays(studiosBookings, monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const sat = {};
  if (studiosBookings.length < 2) return sat;
  for (let d = 1; d <= daysInMonth; d++) {
    const dayStart = new Date(year, month, d).getTime();
    const dayEnd = dayStart + 86400000 - 1;
    let reservedCount = 0;
    for (const bks of studiosBookings) {
      const hasReserved = (bks || []).some(b => {
        const guestRaw = b.guestName || b.guest_name || '';
        const typeRaw = (b.bookingType || b.booking_type || '').toLowerCase();
        // MISMA detección que events.map
        const isBlocked = typeRaw === 'blocked' || /not available|unavailable|blocked|no disponible/i.test(guestRaw);
        if (isBlocked) return false;
        const isConfirmed = (b.status || 'confirmed') === 'confirmed';
        if (!isConfirmed) return false;
        const ci = new Date(b.checkIn).getTime();
        const co = new Date(b.checkOut).getTime();
        return ci <= dayEnd && co > dayStart;
      });
      if (hasReserved) reservedCount++;
    }
    sat[d] = (reservedCount === studiosBookings.length);
  }
  return sat;
}

function renderMiniCalendar(card, property, bookings, monthDate, saturatedDays, isFillerSlot, slotIndex, container) {
  card.innerHTML = '';
  // Dropdown selector
  const selectRow = h('div', { class: 'flex items-center gap-2 mb-3' });
  const select = h('select', {
    class: 'flex-1 bg-base-900 border border-base-700 rounded-lg px-2 py-1.5 text-sm text-ink-100 focus:outline-none focus:border-gold-400',
    onchange: (e) => { _calSlots[slotIndex] = e.target.value; renderCalendars(container); }
  });
  for (const p of _calAllProps) {
    const opt = h('option', { value: p.id }, (_isFillerProp(p) ? '🎯 ' : '🏠 ') + p.name);
    if (property && p.id === property.id) opt.selected = true;
    select.appendChild(opt);
  }
  selectRow.appendChild(h('span', { class: 'text-xs text-ink-300 font-semibold whitespace-nowrap' }, '#' + (slotIndex + 1)));
  selectRow.appendChild(select);
  card.appendChild(selectRow);

  if (!property) {
    card.appendChild(h('div', { class: 'text-ink-300 text-sm text-center py-6' }, 'Selecciona un anuncio'));
    return;
  }

  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekday = (firstDay.getDay() + 6) % 7;

  const events = (bookings || []).map(b => {
    const guestRaw = b.guestName || b.guest_name || '';
    const typeRaw = (b.bookingType || b.booking_type || '').toLowerCase();
    // Detección ROBUSTA de blocked: campo explicito O guest "not available"
    let type = 'reserved';
    if (typeRaw === 'blocked' || /not available|unavailable|blocked|no disponible/i.test(guestRaw)) {
      type = 'blocked';
    }
    return {
      checkIn: new Date(b.checkIn),
      checkOut: new Date(b.checkOut),
      guest: guestRaw,
      source: b.source || '',
      type: type,
      status: b.status || 'confirmed'
    };
  }).filter(e => e.status === 'confirmed');

  function bookingsOnDay(d) {
    const day = new Date(year, month, d);
    const dayStart = day.getTime();
    const dayEnd = dayStart + 86400000 - 1;
    return events.filter(e => e.checkIn.getTime() <= dayEnd && e.checkOut.getTime() > dayStart);
  }

  let reservedNights = 0, manualBlockedN = 0, autoBlockedN = 0;
  // Necesitamos calcular ANTES del loop pero isAutoBlockedDay solo se define en el loop principal,
  // así que lo replicamos aquí inline
  const reservasOrd_h = events.filter(e => e.type === 'reserved').sort((a, b) => a.checkIn.getTime() - b.checkIn.getTime());
  function isAutoBlockedDay_h(d) {
    if (isFillerSlot) return false;
    const dayTs = new Date(year, month, d).getTime();
    let prev = null;
    for (const r of reservasOrd_h) {
      if (r.checkOut.getTime() <= dayTs) prev = r;
      else break;
    }
    const next = reservasOrd_h.find(r => r.checkIn.getTime() > dayTs);
    if (!prev || !next) return false;
    return (next.checkIn.getTime() - prev.checkOut.getTime()) / 86400000 < 10;
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const on = bookingsOnDay(d);
    if (on.some(b => b.type === 'reserved')) reservedNights++;
    else if (on.some(b => b.type === 'blocked')) {
      if (isAutoBlockedDay_h(d)) autoBlockedN++;
      else manualBlockedN++;
    }
  }
  const blockedNights = manualBlockedN + autoBlockedN;
  const occupancy = Math.round((reservedNights / daysInMonth) * 100);

  // Header propiedad
  card.appendChild(h('div', { class: 'flex items-start justify-between mb-3 gap-2' }, [
    h('div', { class: 'min-w-0 flex-1' }, [
      h('div', { class: 'font-semibold text-ink-50 text-sm truncate' }, (isFillerSlot ? '🎯 ' : '🏠 ') + property.name),
      h('div', { class: 'text-xs text-ink-300' }, reservedNights + ' res · ' + manualBlockedN + ' bloq · ' + autoBlockedN + ' auto'),
    ]),
    h('div', { class: 'text-right' }, [
      h('div', { class: 'text-lg font-bold ' + (occupancy >= 80 ? 'text-emerald-300' : occupancy >= 50 ? 'text-gold-300' : 'text-rose-300') }, occupancy + '%'),
      h('div', { class: 'text-[10px] text-ink-300 uppercase tracking-wide' }, 'ocupación'),
    ]),
  ]));

  // Cabecera días
  const dowHeader = h('div', { class: 'grid grid-cols-7 gap-1 mb-1 text-center text-[10px] text-ink-300 font-semibold uppercase' });
  ['L', 'M', 'X', 'J', 'V', 'S', 'D'].forEach(d => dowHeader.appendChild(h('div', {}, d)));
  card.appendChild(dowHeader);

  const grid = h('div', { class: 'grid grid-cols-7 gap-1' });
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  for (let i = 0; i < startWeekday; i++) grid.appendChild(h('div', { class: 'aspect-square' }));

  // Para los estudios (no filler), pre-calcular qué días bloqueados son AUTO (por regla 10 noches)
  // vs MANUAL. Auto-blocked = blocked en gap < 10 noches entre 2 reservas.
  const reservasOrdenadas = events.filter(e => e.type === 'reserved').sort((a, b) => a.checkIn.getTime() - b.checkIn.getTime());
  function isAutoBlockedDay(d) {
    if (isFillerSlot) return false; // filler no tiene regla 10
    const dayTs = new Date(year, month, d).getTime();
    // Reserva anterior (checkout <= dayTs)
    let prev = null;
    for (const r of reservasOrdenadas) {
      if (r.checkOut.getTime() <= dayTs) prev = r;
      else break;
    }
    // Reserva posterior (checkin > dayTs)
    const next = reservasOrdenadas.find(r => r.checkIn.getTime() > dayTs);
    if (!prev || !next) return false;
    const gapDays = (next.checkIn.getTime() - prev.checkOut.getTime()) / 86400000;
    return gapDays < 10;
  }

  let totalSaturated = 0;
  let totalAutoBlocked = 0;
  let totalManualBlocked = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const onDay = bookingsOnDay(d);
    const isToday = isCurrentMonth && today.getDate() === d;
    const reserved = onDay.find(b => b.type === 'reserved');
    const blocked = onDay.find(b => b.type === 'blocked');
    // Auto-bloqueado por regla 10 noches:
    //   - Si está blocked en iCal y el gap es <10 → auto
    //   - O si está LIBRE pero entre 2 reservas con gap <10 → también auto (Airbnb no emite eventos para esto)
    const autoBlockedByRule = !isFillerSlot && isAutoBlockedDay(d);
    const isAutoBlocked = (blocked && autoBlockedByRule) || (!reserved && !blocked && autoBlockedByRule);
    const isManualBlocked = blocked && !autoBlockedByRule;
    const isSaturated = isFillerSlot && saturatedDays[d];
    if (isSaturated) totalSaturated++;
    if (isAutoBlocked) totalAutoBlocked++;
    if (isManualBlocked) totalManualBlocked++;

    let cls = 'aspect-square flex items-center justify-center text-xs rounded-md ';
    let style = '';
    let title = 'Libre';

    const fechaStr = new Date(year, month, d).toLocaleDateString('es-ES', { weekday:'long', day:'numeric', month:'long' });
    cls += ' as-cal-cell cursor-help relative';
    if (reserved) {
      cls += ' bg-emerald-500/70 text-white font-semibold';
      const nights = Math.ceil((reserved.checkOut.getTime() - reserved.checkIn.getTime()) / 86400000);
      const ciStr = reserved.checkIn.toLocaleDateString('es-ES', { day:'numeric', month:'short' });
      const coStr = reserved.checkOut.toLocaleDateString('es-ES', { day:'numeric', month:'short' });
      title = '✅ RESERVADO\n' + fechaStr + '\nHuésped: ' + (reserved.guest || '—') + '\nEstancia: ' + ciStr + ' → ' + coStr + ' (' + nights + ' noches)';
    } else if (isAutoBlocked) {
      cls += ' text-white font-semibold';
      style = 'background-image: repeating-linear-gradient(45deg, #f59e0b 0px, #f59e0b 3px, #92400e 3px, #92400e 7px); background-color: #b45309;';
      title = '🤖 AUTO-BLOQUEADO POR AIRBNB\n' + fechaStr + '\nAirbnb bloquea este día porque el hueco entre dos reservas es menor a 10 noches (tu mínimo).\n💡 Sugerencia: puedes mover esta reserva al Anuncio 4 (sin mínimo) para no perder este día.';
    } else if (isManualBlocked) {
      cls += ' text-white font-semibold';
      style = 'background-image: repeating-linear-gradient(45deg, #94a3b8 0px, #94a3b8 3px, #475569 3px, #475569 7px); background-color: #64748b;';
      title = '⛔ BLOQUEADO MANUALMENTE\n' + fechaStr + '\nTú has bloqueado este día en Airbnb (no se puede reservar).';
    } else if (isSaturated && !reserved && !blocked) {
      cls += ' bg-rose-600 text-white font-bold ring-1 ring-rose-300';
      title = '🔥 SIN SITIO FÍSICO\n' + fechaStr + '\nLos 3 estudios (E1+E2+E3) están reservados este día.\nNo puedes aceptar una reserva nueva en el Anuncio 4 — no habría dónde alojar al huésped.';
    } else {
      cls += ' bg-base-700/40 text-ink-300 hover:bg-base-600/40';
      title = '🟦 LIBRE\n' + fechaStr + '\nDisponible para reservar.';
    }
    if (isToday) { cls += ' ring-2 ring-gold-400'; title = '⭐ HOY · ' + title; }

    const cellAttrs = { class: cls, title, onclick: () => toggleManualBlock(property, year, month, d, container) };
    if (style) cellAttrs.style = style;
    grid.appendChild(h('div', cellAttrs, String(d)));
  }
  card.appendChild(grid);

  // Footer
  if (isFillerSlot && totalSaturated > 0) {
    card.appendChild(h('div', { class: 'mt-3 pt-3 border-t border-base-700 text-xs flex justify-between items-center' }, [
      h('span', { class: 'text-rose-300 font-semibold' }, '⚠️ ' + totalSaturated + ' días sin sitio físico'),
      h('span', { class: 'text-ink-300' }, '(3 estudios llenos)'),
    ]));
  } else {
    const future = events.filter(e => e.type === 'reserved' && e.checkIn.getTime() > Date.now()).sort((a, b) => a.checkIn - b.checkIn);
    if (future.length > 0) {
      const next = future[0];
      const nights = Math.ceil((next.checkOut - next.checkIn) / 86400000);
      card.appendChild(h('div', { class: 'mt-3 pt-3 border-t border-base-700 text-xs text-ink-300 flex justify-between' }, [
        h('span', {}, '📥 Próxima: ' + next.checkIn.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })),
        h('span', { class: 'font-semibold text-ink-100' }, nights + ' noches'),
      ]));
    } else {
      card.appendChild(h('div', { class: 'mt-3 pt-3 border-t border-base-700 text-xs text-ink-300 text-center' }, '— sin próximas reservas —'));
    }
  }
}


// =============== EMAIL MASIVO GMAIL (BCC) ===============
function openEmailMasivoModal(container, contactos, tplData) {
  const conEmail = (contactos || []).filter(c => c.email && c.email.includes('@'));
  const emails = conEmail.map(c => c.email);

  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };

  const modal = document.createElement('div');
  modal.className = 'bg-base-800 border border-gold-400/40 rounded-2xl p-6 max-w-2xl w-full my-8';
  modal.onclick = (e) => e.stopPropagation();

  const tplDefault = (tplData && tplData.template) || '';
  const subjectDefault = 'Búsqueda activa de inmuebles en Alicante centro - Inversor particular';
  const bodyDefault = `Hola,

Soy Carlos García, inversor particular en Alicante. Estoy buscando activamente inmuebles para comprar (zona centro, Carolinas, San Blas, San Vicente del Raspeig).

Lo que busco:
- Pisos para reformar o ya listos
- Operaciones rápidas (puedo cerrar en 30-45 días con financiación propia)
- Margen para acuerdo si la operación es directa

Si tenéis alguna propiedad antes de subirla a los portales (off-market), o algún cliente vendedor con prisa, agradecería que me la comentéis.

Puedo aportar nota simple de mi vivienda actual (tasada 222.000€) como prueba de solvencia.

Teléfono: +34 656 580 025 (WhatsApp)
Email: garciagrouphold@gmail.com

Un saludo,
Carlos García Torres`;

  modal.innerHTML = `
    <h3 class="font-display font-bold text-xl mb-2">📧 Email masivo a inmobiliarias</h3>
    <p class="text-sm text-ink-300 mb-4">Abre Gmail con los ${emails.length} emails en BCC + plantilla. Tú revisas y envías cuando quieras.</p>

    <div class="bg-blue-500/10 border border-blue-400/30 rounded-lg p-3 mb-4 text-xs text-blue-200">
      💡 <strong>Consejo:</strong> Gmail permite enviar hasta 500 destinatarios/día desde cuenta personal. Si pasas de eso, reparte en 2 días.
    </div>

    <div class="mb-3">
      <label class="block text-sm font-semibold mb-1">Tu email remitente</label>
      <input id="em_from" type="email" value="garciagrouphold@gmail.com" class="w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm">
    </div>

    <div class="mb-3">
      <label class="block text-sm font-semibold mb-1">Asunto</label>
      <input id="em_subject" type="text" value="${subjectDefault}" class="w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm">
    </div>

    <div class="mb-3">
      <label class="block text-sm font-semibold mb-1">Cuerpo</label>
      <textarea id="em_body" rows="14" class="w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-xs font-mono">${bodyDefault}</textarea>
    </div>

    <div class="mb-4">
      <label class="block text-sm font-semibold mb-1">Destinatarios BCC (${emails.length})</label>
      <div class="bg-base-900 border border-base-600 rounded-lg px-3 py-2 max-h-24 overflow-y-auto text-xs text-ink-300">
        ${emails.join(', ') || '<em>Sin emails — añade emails a tus contactos primero</em>'}
      </div>
    </div>

    <div class="flex gap-2 flex-wrap">
      <button id="em_copy_emails" class="px-4 py-2 bg-base-700 text-ink-100 rounded-lg hover:bg-base-600 text-sm">📋 Copiar solo emails</button>
      <button id="em_gmail" class="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-400 text-sm font-bold">📧 Abrir en Gmail con todo precargado</button>
      <button id="em_cancel" class="px-4 py-2 bg-base-700 text-ink-100 rounded-lg hover:bg-base-600 text-sm ml-auto">Cancelar</button>
    </div>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  modal.querySelector('#em_cancel').onclick = () => overlay.remove();

  modal.querySelector('#em_copy_emails').onclick = async () => {
    try {
      await navigator.clipboard.writeText(emails.join(', '));
      toast(`✓ ${emails.length} emails copiados al portapapeles`, 'success');
    } catch(e) { toast('No se pudo copiar', 'error'); }
  };

  modal.querySelector('#em_gmail').onclick = () => {
    const from = modal.querySelector('#em_from').value;
    const subject = encodeURIComponent(modal.querySelector('#em_subject').value);
    const body = encodeURIComponent(modal.querySelector('#em_body').value);
    const bcc = encodeURIComponent(emails.join(','));
    // Gmail compose URL — abre el composer con TO=tu propio email, BCC=todos los demás
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(from)}&bcc=${bcc}&su=${subject}&body=${body}`;
    window.open(url, '_blank');
    overlay.remove();
  };
}

// ============================== TAB: 📦 Inventario Airbnb ==============================
async function renderInventario(container) {
  container.innerHTML = '';
  const wrap = h('div', {});
  container.appendChild(wrap);

  // Cargar items + stats + propiedades
  const [itemsResp, statsResp, propsResp] = await Promise.all([
    apiClient.get('/api/v1/inmuebles/inventario').catch(() => ({ items: [] })),
    apiClient.get('/api/v1/inmuebles/inventario/stats').catch(() => ({})),
    apiClient.get('/api/v1/properties').catch(() => ({ data: [] })),
  ]);
  const items = itemsResp.items || [];
  const stats = statsResp || {};
  const properties = propsResp.data || [];

  // Header
  wrap.appendChild(h('div', { class: 'flex items-start justify-between flex-wrap gap-3 mb-4' }, [
    h('div', {}, [
      h('h3', { class: 'font-display font-bold text-2xl mb-1' }, '📦 Inventario'),
      h('p', { class: 'text-sm text-ink-300' }, 'Mobiliario, electrodomésticos y decoración de tus Airbnb · con facturas adjuntas'),
    ]),
    h('button', { class: 'px-4 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400 text-sm',
      onclick: () => openInventarioModal(null, properties, () => renderInventario(container)) }, '+ Añadir cosa'),
  ]));

  // Stats banner
  wrap.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-3 gap-3 mb-5' }, [
    h('div', { class: 'bg-base-800/60 border border-base-700 rounded-xl p-3 text-center' }, [
      h('div', { class: 'text-2xl font-bold text-ink-100' }, String(stats.total_items ?? 0)),
      h('div', { class: 'text-xs text-ink-300 uppercase tracking-wide' }, 'Cosas totales'),
    ]),
    h('div', { class: 'bg-base-800/60 border border-gold-400/40 rounded-xl p-3 text-center' }, [
      h('div', { 'data-stat-valor': '1', class: 'text-2xl font-bold text-gold-200' }, formatEur(stats.total_eur || 0)),
      h('div', { class: 'text-xs text-ink-300 uppercase tracking-wide' }, 'Valor invertido'),
    ]),
    h('div', { class: 'bg-base-800/60 border border-base-700 rounded-xl p-3 text-center' }, [
      h('div', { 'data-stat-factura': '1', class: 'text-2xl font-bold text-emerald-300' }, String(items.filter(i => i.attachment_url).length)),
      h('div', { class: 'text-xs text-ink-300 uppercase tracking-wide' }, 'Con factura'),
    ]),
  ]));

  if (items.length === 0) {
    wrap.appendChild(emptyState('📦', 'Inventario vacío', 'Añade tu primera cosa con el botón + arriba.'));
    return;
  }

  // Filtros por propiedad + uso (en uso / en almacén) + búsqueda + modo agrupación
  let _filterProp = '';
  let _filterUso = 'en_uso';
  let _searchQ = '';
  let _groupMode = 'ubicacion';

  // SUB-TABS: En uso / En almacén
  const subTabs = h('div', { class: 'flex gap-2 mb-4 border-b border-base-700' });
  function refreshSubTabs() {
    subTabs.innerHTML = '';
    const enUsoCount = items.filter(i => (i.uso_estado || 'en_uso') === 'en_uso').length;
    const almacenCount = items.filter(i => i.uso_estado === 'almacen').length;
    [
      { v: 'en_uso',  l: '🏠 En uso (' + enUsoCount + ')' },
      { v: 'almacen', l: '📦 En almacén (' + almacenCount + ')' },
      { v: 'all',     l: '📋 Todo (' + items.length + ')' },
    ].forEach((tab) => {
      const active = _filterUso === tab.v;
      subTabs.appendChild(h('button', {
        class: 'px-4 py-2 text-sm font-semibold transition border-b-2 ' + (active
          ? 'text-gold-300 border-gold-400'
          : 'text-ink-300 border-transparent hover:text-ink-100'),
        onclick: () => { _filterUso = tab.v; refreshSubTabs(); refreshList(); }
      }, tab.l));
    });
  }
  refreshSubTabs();
  wrap.appendChild(subTabs);
  const filterBar = h('div', { class: 'flex gap-2 mb-4 flex-wrap' });
  // Filtros por propiedad ocultos — todos los inmuebles son del mismo edificio Vázquez Mella
  // El inventario ya se agrupa por zona (Pasillo, Terraza piscina, Cocina, etc.) — más útil.
  function refreshFilter() { filterBar.innerHTML = ''; }
  refreshFilter();

  const listEl = h('div', { class: 'space-y-2' });
  wrap.appendChild(listEl);

  function refreshList() {
    listEl.innerHTML = '';
    let filtered = items;
    if (_filterUso === 'en_uso')  filtered = filtered.filter(i => (i.uso_estado || 'en_uso') === 'en_uso');
    if (_filterUso === 'almacen') filtered = filtered.filter(i => i.uso_estado === 'almacen');
    const q = (_searchQ || '').trim().toLowerCase();
    if (q) filtered = filtered.filter(i => (i.nombre || '').toLowerCase().includes(q) || (i.ubicacion || '').toLowerCase().includes(q) || (i.categoria || '').toLowerCase().includes(q));

    // Si está en EN ALMACÉN: mostrar sub-secciones aunque estén vacías (para guiar a Carlos)
    if (_filterUso !== 'almacen' && filtered.length === 0) {
      listEl.appendChild(h('div', { class: 'text-center py-8 text-ink-300' }, 'Sin resultados'));
      return;
    }

    // === MODO: POR CATEGORÍA ===
    if (_groupMode === 'categoria') {
      const CAT_LABEL = {
        'menaje': '🍽️ Menaje y complementos',
        'mobiliario': '🛋️ Mobiliario',
        'electrodomestico': '🍳 Electrodomésticos',
        'electronica': '📺 Electrónica',
        'decoracion': '🖼️ Decoración',
        'ropa-hogar': '🛏️ Ropa de hogar',
        'limpieza': '🧹 Limpieza',
        'otro': '📦 Otro',
      };
      const CAT_ORDER = ['menaje','mobiliario','electrodomestico','electronica','decoracion','ropa-hogar','limpieza','otro'];

      // Agrupar por categoría y dentro por nombre (sumar cantidades)
      const byCat = {};
      filtered.forEach((it) => {
        const c = it.categoria || 'otro';
        if (!byCat[c]) byCat[c] = {};
        const key = it.nombre || '?';
        if (!byCat[c][key]) byCat[c][key] = { nombre: key, cantidad: 0, items: [], precio: 0 };
        byCat[c][key].cantidad += (it.cantidad || 0);
        byCat[c][key].precio += (it.precio_total_eur || 0);
        byCat[c][key].items.push(it);
      });

      const sortedCats = Object.keys(byCat).sort((a,b) => {
        const ia = CAT_ORDER.indexOf(a); const ib = CAT_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });

      sortedCats.forEach((cat) => {
        const productos = Object.values(byCat[cat]).sort((a,b) => b.cantidad - a.cantidad);
        const totalU = productos.reduce((s, p) => s + p.cantidad, 0);
        const totalE = productos.reduce((s, p) => s + p.precio, 0);

        const det = document.createElement('details');
        det.className = 'mb-3 bg-base-800/40 rounded-2xl border border-base-700 overflow-hidden';
        det.open = !!q;
        const sum = document.createElement('summary');
        sum.className = 'cursor-pointer list-none flex items-center justify-between bg-gradient-to-r from-purple-500/15 to-purple-500/5 hover:from-purple-500/25 px-5 py-3';
        sum.innerHTML = '<div class="flex items-center gap-3"><h3 class="font-display font-bold text-base text-purple-200">' + (CAT_LABEL[cat] || cat) + '</h3><span class="text-xs text-ink-300">' + productos.length + ' tipos · ' + totalU + ' unidades' + (totalE > 0 ? ' · ' + totalE.toFixed(0) + '€' : '') + '</span></div><span class="text-purple-300">▼</span>';
        det.appendChild(sum);

        const body = h('div', { class: 'divide-y divide-base-700/50' });
        let rendered = false;
        const renderProductos = () => {
          productos.forEach((p) => {
            const row = h('div', { class: 'flex items-center justify-between gap-3 px-5 py-2.5 hover:bg-base-700/30' }, [
              h('div', { class: 'flex-1' }, [
                h('div', { class: 'font-medium text-ink-100 text-sm' }, p.nombre),
                h('div', { class: 'text-xs text-ink-400 mt-0.5' }, p.items.map((i) => {
                  const inm = i.property_id === 'prp_9mflqpagt0lh' ? 'E1' : i.property_id === 'prp_97baf9956b3c' ? 'E2' : i.property_id === 'prp_4df9db142d47' ? 'E3' : 'Común';
                  return inm + ' ×' + (i.cantidad || 0);
                }).join(' · ')),
              ]),
              h('div', { class: 'text-right' }, [
                h('div', { class: 'text-base font-bold text-gold-200' }, '×' + p.cantidad),
                h('div', { class: 'text-xs text-ink-400' }, p.precio > 0 ? p.precio.toFixed(0) + '€' : '—'),
              ]),
            ]);
            body.appendChild(row);
          });
        };
        det.addEventListener('toggle', () => { if (det.open && !rendered) { renderProductos(); rendered = true; } });
        if (!!q) { renderProductos(); rendered = true; }
        det.appendChild(body);
        listEl.appendChild(det);
      });
      return;
    }
    // === MODO EN USO: Sub-sección Limpieza al inicio + resto por inmueble/zona ===
    if (_filterUso === 'en_uso') {
      const limpiezaUso = filtered.filter(i => (i.categoria || '') === 'limpieza');
      if (limpiezaUso.length > 0) {
        const totalU = limpiezaUso.reduce((s, i) => s + (i.cantidad || 0), 0);
        const det = document.createElement('details');
        det.className = 'mb-4 bg-base-900/60 rounded-2xl border-2 border-cyan-400/30 overflow-hidden';
        det.open = false;
        const sum = document.createElement('summary');
        sum.className = 'cursor-pointer list-none flex items-center justify-between bg-gradient-to-r from-cyan-500/20 to-cyan-500/5 hover:from-cyan-500/30 px-5 py-4';
        sum.innerHTML = '<div class="flex items-center gap-3"><h3 class="font-display font-bold text-lg text-cyan-100">🧴 Limpieza</h3><span class="text-xs text-ink-300 bg-base-800/60 rounded-full px-3 py-1">' + limpiezaUso.length + ' items · ' + totalU + ' unidades</span></div><span class="text-cyan-300 text-base">▾</span>';
        det.appendChild(sum);
        const body = h('div', { class: 'divide-y divide-base-700/50' });
        let rendered = false;
        det.addEventListener('toggle', () => {
          if (det.open && !rendered) {
            limpiezaUso.forEach((it) => body.appendChild(inventarioCompactRow(it, properties, () => renderInventario(container))));
            rendered = true;
          }
        });
        det.appendChild(body);
        listEl.appendChild(det);
      }
      // Filtrar Limpieza del resto para que no se duplique
      filtered = filtered.filter(i => (i.categoria || '') !== 'limpieza');
    }

    // === MODO ALMACÉN: doble sub-sección Limpieza / Recambios ===
    if (_filterUso === 'almacen') {
      const limpieza = filtered.filter(i => (i.categoria || '') === 'limpieza');
      const recambios = filtered.filter(i => (i.categoria || '') !== 'limpieza');

      [
        { key: 'limpieza',  label: '🧴 Limpieza',  emoji: '🧴', items: limpieza, color: 'cyan' },
        { key: 'recambios', label: '🔄 Recambios', emoji: '🔄', items: recambios, color: 'amber' },
      ].forEach((grp) => {
        if (grp.items.length === 0 && filtered.length > 0) return;
        const totalU = grp.items.reduce((s, i) => s + (i.cantidad || 0), 0);
        const det = document.createElement('details');
        det.className = 'mb-4 bg-base-900/60 rounded-2xl border-2 border-' + grp.color + '-400/30 overflow-hidden';
        det.open = true;
        const sum = document.createElement('summary');
        sum.className = 'cursor-pointer list-none flex items-center justify-between bg-gradient-to-r from-' + grp.color + '-500/20 to-' + grp.color + '-500/5 hover:from-' + grp.color + '-500/30 px-5 py-4';
        sum.innerHTML = '<div class="flex items-center gap-3"><h3 class="font-display font-bold text-lg text-' + grp.color + '-100">' + grp.label + '</h3><span class="text-xs text-ink-300 bg-base-800/60 rounded-full px-3 py-1">' + grp.items.length + ' items · ' + totalU + ' unidades</span></div><span class="text-' + grp.color + '-300 text-base">▾</span>';
        det.appendChild(sum);
        const body = h('div', { class: 'divide-y divide-base-700/50' });
        if (grp.items.length === 0) {
          body.appendChild(h('div', { class: 'text-center py-6 text-ink-400 text-sm italic' }, 'Sin items aún · pulsa "+ Añadir cosa" con categoría ' + (grp.key === 'limpieza' ? '🧹 Limpieza' : 'cualquier otra (toallas, menaje, ropa…)') + ' y estado 📦 En almacén'));
        } else {
          grp.items.forEach((it) => body.appendChild(inventarioCompactRow(it, properties, () => renderInventario(container))));
        }
        det.appendChild(body);
        listEl.appendChild(det);
      });
      return;
    }

    // === MODO: POR UBICACIÓN (resto del código sigue abajo) ===

    // === NIVEL 1: INMUEBLE === / === NIVEL 2: ZONA ===
    const INMUEBLE_ORDER = ['__common__', 'prp_9mflqpagt0lh', 'prp_97baf9956b3c', 'prp_4df9db142d47', 'prp_anuncio4_lau'];
    const INMUEBLE_LABEL = {
      '__common__': '🏠 Áreas comunes (los 3 estudios)',
      'prp_9mflqpagt0lh': '🛏️ Estudio 1',
      'prp_97baf9956b3c': '🛏️ Estudio 2',
      'prp_4df9db142d47': '🛏️ Estudio 3',
      'prp_anuncio4_lau': '🛏️ Anuncio 4 (filler)',
    };
    const ZONE_ORDER = ['Pasillo','Terraza piscina','Ropa de cama (común)','Toallas (común)','Cocina','Baño','Dormitorio','Salón','Armario','Terraza Estudio 1','Terraza Estudio 2','Terraza Estudio 3'];
    const ZONE_EMOJI = { 'Pasillo':'🛗','Terraza piscina':'🏊','Ropa de cama (común)':'🛏️','Toallas (común)':'🧖','Cocina':'🍳','Baño':'🚿','Dormitorio':'🛌','Salón':'🛋️','Armario':'👔','Terraza Estudio 1':'🌅','Terraza Estudio 2':'🌅','Terraza Estudio 3':'🌅' };

    // Agrupar nivel 1: por inmueble
    const byInmueble = {};
    filtered.forEach((it) => {
      const inm = it.property_id || '__common__';
      if (!byInmueble[inm]) byInmueble[inm] = [];
      byInmueble[inm].push(it);
    });

    const sortedInmuebles = Object.keys(byInmueble).sort((a,b) => {
      const ia = INMUEBLE_ORDER.indexOf(a); const ib = INMUEBLE_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

    sortedInmuebles.forEach((inmId) => {
      const inmItems = byInmueble[inmId];
      const totalInmU = inmItems.reduce((s, i) => s + (i.cantidad || 0), 0);
      const inmLabel = INMUEBLE_LABEL[inmId] || '🏠 Otro';

      // Bloque inmueble (nivel 1) — siempre abierto si hay búsqueda
      const inmDetails = document.createElement('details');
      inmDetails.className = 'mb-4 bg-base-900/60 rounded-2xl border-2 border-gold-400/30 overflow-hidden';
      inmDetails.open = !!q || sortedInmuebles.length === 1;
      const inmSummary = document.createElement('summary');
      inmSummary.className = 'cursor-pointer list-none flex items-center justify-between bg-gradient-to-r from-gold-500/20 to-gold-500/5 hover:from-gold-500/30 px-5 py-4';
      inmSummary.innerHTML = '<div class="flex items-center gap-3"><h3 class="font-display font-bold text-lg text-gold-100">' + inmLabel + '</h3><span class="text-xs text-ink-300 bg-base-800/60 rounded-full px-3 py-1">' + inmItems.length + ' items · ' + totalInmU + ' unidades</span></div><span class="text-gold-300 text-base">▾</span>';
      inmDetails.appendChild(inmSummary);

      const inmBody = h('div', { class: 'p-3 space-y-2' });

      // Sub-agrupar por zona dentro del inmueble
      const byZone = {};
      inmItems.forEach((it) => {
        const z = it.ubicacion || '(Sin zona)';
        if (!byZone[z]) byZone[z] = [];
        byZone[z].push(it);
      });
      const sortedZones = Object.keys(byZone).sort((a,b) => {
        const ia = ZONE_ORDER.indexOf(a); const ib = ZONE_ORDER.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });

      sortedZones.forEach((zona) => {
        const zItems = byZone[zona];
        const zU = zItems.reduce((s, i) => s + (i.cantidad || 0), 0);
        const emoji = ZONE_EMOJI[zona] || '📍';
        const zDetails = document.createElement('details');
        zDetails.className = 'bg-base-800/40 rounded-xl border border-base-700 overflow-hidden';
        zDetails.open = !!q;
        const zSum = document.createElement('summary');
        zSum.className = 'cursor-pointer list-none flex items-center justify-between bg-base-800/70 hover:bg-base-800 px-4 py-2.5';
        zSum.innerHTML = '<div class="flex items-center gap-2"><span class="text-base">' + emoji + '</span><span class="font-semibold text-sm text-ink-100">' + zona + '</span><span class="text-xs text-ink-400 ml-2">' + zItems.length + ' items · ' + zU + ' uds</span></div><span class="text-ink-400 text-xs">▼</span>';
        zDetails.appendChild(zSum);
        // Lista compacta (no grid)
        const list = h('div', { class: 'divide-y divide-base-700/50' });
        let renderedZ = false;
        zDetails.addEventListener('toggle', () => {
          if (zDetails.open && !renderedZ) {
            zItems.forEach((it) => list.appendChild(inventarioCompactRow(it, properties, () => renderInventario(container))));
            renderedZ = true;
          }
        });
        if (!!q) {
          zItems.forEach((it) => list.appendChild(inventarioCompactRow(it, properties, () => renderInventario(container))));
          renderedZ = true;
        }
        zDetails.appendChild(list);
        inmBody.appendChild(zDetails);
      });

      inmDetails.appendChild(inmBody);
      listEl.appendChild(inmDetails);
    });
  }
  refreshList();
}

function inventarioCard(it, properties, onReload) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 hover:border-gold-400/40 rounded-xl p-3 transition group flex items-start gap-3' }, [
    h('div', { class: 'flex-1 min-w-0' }, [
      h('div', { class: 'flex items-start justify-between gap-2' }, [
        h('div', {}, [
          h('div', { class: 'font-semibold text-ink-100' }, it.nombre),
          h('div', { class: 'text-xs text-ink-300 mt-0.5 flex flex-wrap gap-2' }, [
            it.property_name ? h('span', { class: 'bg-blue-500/15 text-blue-300 border border-blue-400/30 rounded-full px-2 py-0.5' }, '🏠 ' + it.property_name) : null,
            it.categoria ? h('span', { class: 'bg-purple-500/15 text-purple-300 border border-purple-400/30 rounded-full px-2 py-0.5' }, it.categoria) : null,
            it.ubicacion ? h('span', { class: 'text-ink-400' }, '📍 ' + it.ubicacion) : null,
          ].filter(Boolean)),
        ]),
        h('div', { class: 'text-right shrink-0' }, [
          h('div', { class: 'text-lg font-bold text-gold-200' }, formatEur(it.precio_total_eur || 0)),
          h('div', { class: 'text-xs text-ink-400' }, (it.cantidad || 1) + 'x · ' + formatEur(it.precio_unitario_eur || 0) + '/u'),
        ]),
      ]),
      it.notas ? h('p', { class: 'text-xs text-ink-400 mt-2' }, it.notas) : null,
      h('div', { class: 'flex items-center gap-2 mt-2 flex-wrap' }, [
        it.attachment_url ? h('a', { href: it.attachment_url, target: '_blank',
          class: 'text-xs bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 rounded-lg px-2 py-1 hover:bg-emerald-500/30' }, '📄 Ver factura') : null,
        h('button', { class: 'text-xs bg-base-700 hover:bg-base-600 border border-base-600 rounded-lg px-2 py-1 text-ink-200',
          onclick: () => openUploadFactura(it.id, onReload) }, it.attachment_url ? '🔄 Cambiar factura' : '📎 Subir factura'),
        h('button', { class: 'text-xs bg-base-700 hover:bg-base-600 border border-base-600 rounded-lg px-2 py-1 text-ink-200',
          onclick: () => openInventarioModal(it, properties, onReload) }, '✏️ Editar'),
        h('button', { class: 'text-xs bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/40 rounded-lg px-2 py-1 text-rose-200',
          onclick: async () => {
            const ok = await openConfirm({ title: 'Eliminar', message: it.nombre, danger: true, icon: '🗑️' });
            if (!ok) return;
            await apiClient.delete('/api/v1/inmuebles/inventario/' + it.id);
            toast('Eliminado', 'success');
            onReload();
          } }, '🗑'),
      ].filter(Boolean)),
    ]),
  ]);
}

function openInventarioModal(existing, properties, onDone) {
  openModal({
    title: existing ? 'Editar item' : 'Añadir al inventario',
    icon: '📦',
    size: 'lg',
    fields: [
      { name: 'nombre', label: 'Nombre', required: true, value: existing?.nombre, placeholder: 'Sofá, TV LG 55", colchón...' },
      { name: 'property_id', label: 'Inmueble', type: 'select', value: existing?.property_id || '',
        options: [{ value: '', label: '— Sin asignar / General —' }, ...properties.map(p => ({ value: p.id, label: '🏠 ' + p.name }))] },
      { name: 'categoria', label: 'Categoría', type: 'select', value: existing?.categoria || '',
        options: [
          { value: '', label: '— Ninguna —' },
          { value: 'mobiliario', label: '🛋️ Mobiliario' },
          { value: 'electrodomestico', label: '🍳 Electrodoméstico' },
          { value: 'decoracion', label: '🖼️ Decoración' },
          { value: 'menaje', label: '🍽️ Menaje cocina' },
          { value: 'ropa-hogar', label: '🛏️ Ropa de hogar' },
          { value: 'electronica', label: '📺 Electrónica' },
          { value: 'limpieza', label: '🧹 Limpieza' },
          { value: 'otro', label: '📦 Otro' },
        ] },
      { name: 'cantidad', label: 'Cantidad', type: 'number', value: existing?.cantidad || 1, min: 1 },
      { name: 'precio_unitario_eur', label: 'Precio por unidad (€)', type: 'number', step: '0.01', value: existing?.precio_unitario_eur || '' },
      { name: 'uso_estado', label: '📍 Estado', type: 'select', value: existing?.uso_estado || 'en_uso', options: [
        { value: 'en_uso',  label: '🏠 En uso (instalado en el piso)' },
        { value: 'almacen', label: '📦 En almacén (guardado para repuesto)' },
      ] },
      { name: 'ubicacion', label: 'Ubicación dentro del piso (opcional)', value: existing?.ubicacion || '', placeholder: 'Salón, dormitorio, cocina...' },
      { name: 'estado', label: 'Estado', type: 'select', value: existing?.estado || 'bueno', options: [
        { value: 'nuevo', label: '✨ Nuevo' },
        { value: 'bueno', label: '👍 Bueno' },
        { value: 'usado', label: '🔁 Usado' },
        { value: 'reparar', label: '🔧 A reparar' },
      ] },
      { name: 'notas', label: 'Notas', type: 'textarea', rows: 2, value: existing?.notas || '' },
    ],
    onSubmit: async (vals) => {
      const payload = {
        nombre: vals.nombre,
        property_id: vals.property_id || null,
        categoria: vals.categoria || null,
        cantidad: Number(vals.cantidad) || 1,
        precio_unitario_eur: vals.precio_unitario_eur ? Number(vals.precio_unitario_eur) : null,
        ubicacion: vals.ubicacion || null,
        uso_estado: vals.uso_estado || 'en_uso',
        estado: vals.estado || 'bueno',
        notas: vals.notas || null,
      };
      if (existing?.id) {
        await apiClient.patch('/api/v1/inmuebles/inventario/' + existing.id, payload);
        toast('Actualizado', 'success');
      } else {
        await apiClient.post('/api/v1/inmuebles/inventario', payload);
        toast('Añadido al inventario', 'success');
      }
      onDone();
    },
  });
}

function openUploadFactura(itemId, onDone) {
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'bg-base-800 border border-gold-400/40 rounded-2xl p-6 max-w-md w-full';
  modal.onclick = (e) => e.stopPropagation();
  modal.innerHTML = `
    <h3 class="font-display font-bold text-xl mb-4">📎 Subir factura/ticket</h3>
    <p class="text-sm text-ink-300 mb-4">PDF, JPG, PNG o HEIC · máx 20MB</p>
    <input type="file" id="upf_file" accept=".pdf,.jpg,.jpeg,.png,.heic,image/*" class="block w-full mb-4 text-sm text-ink-200 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gold-500 file:text-black file:font-bold">
    <div class="flex gap-2">
      <button id="upf_cancel" class="px-4 py-2 bg-base-700 hover:bg-base-600 rounded-lg text-sm flex-1">Cancelar</button>
      <button id="upf_upload" class="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-lg text-sm flex-1">Subir</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  modal.querySelector('#upf_cancel').onclick = () => overlay.remove();
  modal.querySelector('#upf_upload').onclick = async () => {
    const file = modal.querySelector('#upf_file').files[0];
    if (!file) { toast('Selecciona un archivo', 'warning'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await fetch('/api/v1/inmuebles/inventario/' + itemId + '/attachment', { method: 'POST', body: fd, credentials: 'include' });
      if (!resp.ok) throw new Error('upload error');
      toast('Factura subida ✓', 'success');
      overlay.remove();
      onDone();
    } catch(e) { toast('Error subiendo archivo', 'error'); }
  };
}


// Fila compacta para vista jerárquica (más densa que inventarioCard)
function inventarioCompactRow(it, properties, onReload) {
  // función para actualizar TOTAL en pantalla sin re-render
  function recalcTotal() {
    const totalEl = row.querySelector('[data-item-total]');
    const t = it.precio_unitario_eur != null ? it.precio_unitario_eur * (it.cantidad || 1) : null;
    it.precio_total_eur = t;
    if (totalEl) totalEl.textContent = t != null ? formatEur(t) : '';
    // refrescar stats arriba (cosas totales, valor invertido)
    refreshGlobalStats();
  }

  const row = h('div', { class: 'flex items-center justify-between gap-2 px-3 py-2 hover:bg-base-700/30 transition flex-wrap', 'data-row-id': it.id }, [
    h('div', { class: 'flex-1 min-w-0' }, [
      h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
        h('span', { class: 'font-medium text-ink-100 text-sm' }, it.nombre),
        // Cantidad editable inline
        h('input', {
          type: 'number', min: '0', step: '1',
          value: it.cantidad || 1,
          title: 'Cantidad · Enter para guardar',
          class: 'w-14 text-xs bg-gold-500/10 border border-gold-400/30 hover:border-gold-400 focus:border-gold-400 rounded px-1.5 py-0.5 text-gold-200 font-bold text-center',
          onclick: (e) => e.stopPropagation(),
          onkeydown: (e) => { if (e.key === 'Enter') e.target.blur(); },
          onblur: async (e) => {
            const v = e.target.value === '' ? 1 : Math.max(0, parseInt(e.target.value, 10) || 0);
            if (v === (it.cantidad || 1)) return;
            try {
              await apiClient.patch('/api/v1/inmuebles/inventario/' + it.id, { cantidad: v });
              it.cantidad = v;
              recalcTotal();
              toast('Cantidad ' + v, 'success');
            } catch (err) { toast('Error', 'error'); e.target.value = it.cantidad; }
          },
        }),
        it.attachment_url ? h('span', { class: 'text-emerald-300 text-xs', title: 'Tiene factura' }, '📄') : null,
      ].filter(Boolean)),
      it.notas ? h('div', { class: 'text-xs text-ink-400 mt-0.5' }, it.notas) : null,
    ]),
    h('div', { class: 'flex items-center gap-2 shrink-0' }, [
      // Precio €/unidad editable inline (vacío = borra)
      h('input', {
        type: 'number', step: '0.01', min: '0',
        placeholder: '€/u',
        value: it.precio_unitario_eur != null ? it.precio_unitario_eur : '',
        title: 'Precio por unidad · Enter para guardar · vacío para borrar',
        class: 'w-20 text-sm text-gold-200 font-semibold bg-base-900/60 border border-base-700 hover:border-gold-400/40 focus:border-gold-400 rounded px-2 py-1 text-right',
        onclick: (e) => e.stopPropagation(),
        onkeydown: (e) => { if (e.key === 'Enter') e.target.blur(); },
        onblur: async (e) => {
          const raw = e.target.value;
          const v = (raw === '' || raw == null) ? null : Number(raw);
          if (v === (it.precio_unitario_eur ?? null)) return;
          try {
            await apiClient.patch('/api/v1/inmuebles/inventario/' + it.id, { precio_unitario_eur: v });
            it.precio_unitario_eur = v;
            recalcTotal();
            toast(v != null ? '💰 ' + v + '€/u' : '💰 Precio borrado', 'success');
          } catch (err) { toast('Error guardando precio', 'error'); }
        },
      }),
      h('span', { 'data-item-total': '1', class: 'text-xs text-gold-300 font-semibold w-16 text-right' }, it.precio_total_eur ? formatEur(it.precio_total_eur) : ''),
      it.attachment_url
        ? h('a', { href: it.attachment_url, target: '_blank', title: 'Ver factura',
            class: 'text-xs bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 rounded px-2 py-1 text-emerald-200' }, '📄')
        : h('button', { title: 'Subir factura',
            class: 'text-xs bg-base-700 hover:bg-base-600 rounded px-2 py-1 text-ink-200',
            onclick: (e) => { e.stopPropagation(); openUploadFactura(it.id, onReload); } }, '📎'),
      h('button', {
        class: 'text-xs bg-base-700 hover:bg-base-600 rounded px-2 py-1 text-ink-200',
        onclick: (e) => { e.stopPropagation(); openInventarioModal(it, properties, onReload); } }, '✏️'),
      h('button', {
        class: 'text-xs bg-rose-500/20 hover:bg-rose-500/30 rounded px-2 py-1 text-rose-200',
        onclick: async (e) => {
          e.stopPropagation();
          const ok = await openConfirm({ title: 'Eliminar', message: it.nombre, danger: true, icon: '🗑️' });
          if (!ok) return;
          await apiClient.delete('/api/v1/inmuebles/inventario/' + it.id);
          toast('Eliminado', 'success');
          onReload();
        } }, '🗑'),
    ]),
  ]);
  return row;
}

// Refresca el banner de stats arriba (valor invertido, con factura) sin re-render completo
function refreshGlobalStats() {
  try {
    const all = window.__inventarioAllItems || [];
    const totalVal = all.reduce((s, i) => s + (i.precio_total_eur || 0), 0);
    const conFact = all.filter(i => i.attachment_url).length;
    const valEl = document.querySelector('[data-stat-valor]');
    const factEl = document.querySelector('[data-stat-factura]');
    if (valEl) valEl.textContent = formatEur(totalVal);
    if (factEl) factEl.textContent = String(conFact);
  } catch (e) { /* silent */ }
}

// === ARCHIVOS IMPORTANTES de inmuebles ===
async function renderImportantDocs(container) {
  container.innerHTML = '';
  let docs = [];
  try {
    const r = await apiClient.get('/api/v1/inmuebles/documents');
    docs = r.documents || [];
  } catch (e) { docs = []; }

  const propsRes = await apiClient.get('/api/v1/properties').catch(() => ({ data: [] }));
  const properties = propsRes.data || [];

  const card = h('div', { class: 'bg-base-800/50 border border-gold-400/30 rounded-2xl p-5' });
  card.appendChild(h('div', { class: 'flex items-center justify-between mb-3 flex-wrap gap-2' }, [
    h('div', {}, [
      h('h3', { class: 'font-display text-xl font-bold flex items-center gap-2' }, '📁 Archivos importantes'),
      h('p', { class: 'text-xs text-ink-300 mt-1' }, 'Escrituras, notas simples, planos, contratos, tasaciones...'),
    ]),
    h('button', {
      class: 'px-4 py-2 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400 text-sm',
      onclick: () => openDocModal(properties, () => renderImportantDocs(container))
    }, '+ Subir archivo'),
  ]));

  if (docs.length === 0) {
    card.appendChild(h('div', { class: 'text-center py-6 text-ink-300 text-sm' }, [
      h('div', { class: 'text-4xl mb-2' }, '📂'),
      h('div', {}, 'Sin archivos aún. Pulsa "+ Subir archivo" para añadir escrituras, contratos, planos, etc.'),
    ]));
  } else {
    const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
    const TIPO_EMOJI = { escritura:'📜', nota_simple:'📋', plano:'🗺️', contrato_gestion:'📑', contrato_alquiler:'🔑', tasacion:'💰', seguro:'🛡️', factura:'🧾', certificado:'📄', otro:'📁' };
    const TIPO_LABEL = { escritura:'Escritura', nota_simple:'Nota simple', plano:'Plano', contrato_gestion:'Contrato gestión', contrato_alquiler:'Contrato alquiler', tasacion:'Tasación', seguro:'Seguro', factura:'Factura', certificado:'Certificado', otro:'Otro' };
    docs.forEach((d) => {
      const emoji = TIPO_EMOJI[d.tipo] || '📁';
      const tipoLabel = TIPO_LABEL[d.tipo] || (d.tipo || 'Documento');
      grid.appendChild(h('div', { class: 'bg-base-900/60 border border-base-700 hover:border-gold-400/40 rounded-xl p-3 transition' }, [
        h('div', { class: 'flex items-start gap-3 mb-2' }, [
          h('div', { class: 'text-3xl' }, emoji),
          h('div', { class: 'flex-1 min-w-0' }, [
            h('div', { class: 'font-semibold text-ink-100 text-sm break-words' }, d.nombre),
            h('div', { class: 'text-xs text-ink-300 mt-0.5' }, tipoLabel + (d.property_name ? ' · 🏠 ' + d.property_name : ' · 🏠 General')),
            d.descripcion ? h('p', { class: 'text-xs text-ink-400 mt-1' }, d.descripcion) : null,
          ].filter(Boolean)),
        ]),
        h('div', { class: 'flex items-center gap-2 mt-2 flex-wrap' }, [
          d.attachment_url
            ? h('a', { href: d.attachment_url, target: '_blank',
                class: 'text-xs bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 rounded-lg px-3 py-1.5 text-emerald-200 font-semibold' }, '📥 Ver archivo')
            : h('button', { class: 'text-xs bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 rounded-lg px-3 py-1.5 text-gold-200 font-semibold',
                onclick: () => openDocUpload(d.id, () => renderImportantDocs(container)) }, '📎 Subir archivo'),
          h('button', {
            class: 'text-xs bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/40 rounded-lg px-3 py-1.5 text-rose-200 ml-auto',
            onclick: async () => {
              const ok = await openConfirm({ title: 'Eliminar', message: d.nombre, danger: true, icon: '🗑️' });
              if (!ok) return;
              await apiClient.delete('/api/v1/inmuebles/documents/' + d.id);
              toast('Eliminado', 'success');
              renderImportantDocs(container);
            } }, '🗑'),
        ]),
      ]));
    });
    card.appendChild(grid);
  }
  container.appendChild(card);
}

function openDocModal(properties, onDone) {
  openModal({
    title: 'Nuevo documento importante',
    icon: '📁',
    size: 'lg',
    fields: [
      { name: 'nombre', label: 'Nombre', required: true, placeholder: 'Escritura de compraventa V. Mella' },
      { name: 'tipo', label: 'Tipo', type: 'select', value: 'escritura', options: [
        { value: 'escritura', label: '📜 Escritura' },
        { value: 'nota_simple', label: '📋 Nota simple' },
        { value: 'plano', label: '🗺️ Plano' },
        { value: 'contrato_gestion', label: '📑 Contrato gestión' },
        { value: 'contrato_alquiler', label: '🔑 Contrato alquiler' },
        { value: 'tasacion', label: '💰 Tasación' },
        { value: 'seguro', label: '🛡️ Seguro' },
        { value: 'factura', label: '🧾 Factura' },
        { value: 'certificado', label: '📄 Certificado' },
        { value: 'otro', label: '📁 Otro' },
      ] },
      { name: 'property_id', label: 'Inmueble', type: 'select', value: '',
        options: [{ value: '', label: '— General / Todos —' }, ...properties.map(p => ({ value: p.id, label: '🏠 ' + p.name }))] },
      { name: 'descripcion', label: 'Notas (opcional)', type: 'textarea', rows: 2 },
    ],
    onSubmit: async (vals) => {
      const r = await apiClient.post('/api/v1/inmuebles/documents', {
        nombre: vals.nombre, tipo: vals.tipo, property_id: vals.property_id || null, descripcion: vals.descripcion || null,
      });
      toast('Documento creado · ahora sube el archivo', 'success');
      onDone();
      if (r.document && r.document.id) setTimeout(() => openDocUpload(r.document.id, onDone), 200);
    },
  });
}

function openDocUpload(docId, onDone) {
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'bg-base-800 border border-gold-400/40 rounded-2xl p-6 max-w-md w-full';
  modal.onclick = (e) => e.stopPropagation();
  modal.innerHTML = '<h3 class="font-display font-bold text-xl mb-2">📎 Subir archivo</h3><p class="text-sm text-ink-300 mb-4">PDF, JPG, PNG. Máx 25 MB.</p><input id="docfile" type="file" accept="image/*,application/pdf,.heic" class="w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm mb-4"><div class="flex gap-2 justify-end"><button id="cancel" class="px-4 py-2 bg-base-700 hover:bg-base-600 rounded-lg text-sm text-ink-200">Cancelar</button><button id="send" class="px-4 py-2 bg-gold-500 hover:bg-gold-400 rounded-lg text-sm text-black font-semibold">📤 Subir</button></div>';
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  modal.querySelector('#cancel').onclick = () => overlay.remove();
  modal.querySelector('#send').onclick = async () => {
    const fileInput = modal.querySelector('#docfile');
    if (!fileInput.files || fileInput.files.length === 0) { toast('Elige un archivo', 'warning'); return; }
    const file = fileInput.files[0];
    if (file.size > 25 * 1024 * 1024) { toast('Archivo > 25 MB', 'error'); return; }
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await fetch('/api/v1/inmuebles/documents/' + docId + '/attachment', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      toast('📄 Archivo subido', 'success');
      overlay.remove();
      if (onDone) onDone();
    } catch (e) { toast('Error: ' + e.message, 'error'); }
  };
}
